import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { PRACTICE_DIM_OFFSET, type MapDef } from "@neon-purge/shared";
import { mapTextures, surfaceMaps, worldProjectUV, worldTileUV } from "./mapTextures.js";
import { applyWind, uprightSway, windGeometry } from "./wind.js";

/**
 * Purely-decorative map dressing, built once per map load:
 *  - a distant scenery ring outside the walls: by day two layered ridges (near
 *    forested hills + a hazy far ridge for aerial perspective), on neon maps a
 *    tiered city skyline (setback towers + rooftop plant + antenna beacons) that
 *    the fog fades into the horizon
 *  - scattered ground props (rocks/bushes by day, glow bollards + dark rocks
 *    on neon) as a handful of InstancedMeshes
 * Everything is merged/instanced (a handful of draw calls total — the ring is
 * 2 meshes by day, 3 on neon), casts no shadows and has no collision — visual
 * richness at effectively zero frame cost.
 * Deterministic per map id so every client sees the same world.
 */

/** Frequency-band resolution shared by every music visualiser (boombox EQ +
 *  tower-facade histogram). The renderer asks audio for this many bands. */
export const EQ_BANDS = 24;

export function lcg(seedStr: string): () => number {
  let s = 0;
  for (const ch of seedStr) s = (s * 31 + ch.charCodeAt(0)) & 0x7fffffff;
  if (!s) s = 7;
  return () => (s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
}

/** Deterministic radial displacement — turns a platonic solid into a lump.
 *
 *  Displaces along the RADIUS FROM THE GEOMETRY ORIGIN, not along the vertex
 *  normal. `IcosahedronGeometry` is non-indexed, so one corner of the solid
 *  exists as several separate vertices that share a position — and at detail 0
 *  each of those carries its own FLAT FACE normal, so displacing along the
 *  normal sends them different ways and tears the solid into confetti. (At
 *  detail 1, which is what every prop here uses, three normalises the normals
 *  to the radius and the two agree — so this is a trap that is currently
 *  dormant, not a live bug. `agent/bench-vegetation.mjs` check A pins down
 *  both halves of that.) Radial is the direction every duplicate agrees on at
 *  ANY detail level. The amount is hashed from the original position, so the
 *  duplicates also agree on how far — and the whole thing stays a pure function
 *  of (geometry, seed), which keeps decor identical on every client.
 *
 *  This is the non-texture half of the vegetation pass: a perfect icosahedron
 *  reads as a DIE, and no rock texture can fix that silhouette. */
function roughen(geo: THREE.BufferGeometry, amp: number, seed: number): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const len = Math.hypot(x, y, z);
    if (len < 1e-5) continue;
    const s = Math.sin(x * 127.1 + y * 311.7 + z * 74.7 + seed * 19.19) * 43758.5453;
    const d = 1 + (s - Math.floor(s) - 0.5) * 2 * amp;
    pos.setXYZ(i, x * d, y * d, z * d);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/** Lean a vertical part: the top drifts by (dx,dz) with a soft power curve, so
 *  a trunk bends instead of tilting rigidly at its root. */
function bend(geo: THREE.BufferGeometry, dx: number, dz: number, h: number): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const t = Math.max(0, Math.min(1, pos.getY(i) / h)) ** 1.5;
    pos.setX(i, pos.getX(i) + dx * t);
    pos.setZ(i, pos.getZ(i) + dz * t);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/** A tapered limb from A to B — branches, kelp stipes, anything that has to
 *  point somewhere. Non-indexed so it merges with the polyhedron blobs
 *  (`mergeGeometries` returns null on mixed indexing — that null once nulled
 *  `treeGeo` and hung every day map on the connecting screen). */
const LIMB_A = new THREE.Vector3(0, 1, 0);
function limb(
  r0: number, r1: number,
  from: [number, number, number], to: [number, number, number],
  segs = 5,
): THREE.BufferGeometry {
  const dir = new THREE.Vector3(to[0] - from[0], to[1] - from[1], to[2] - from[2]);
  const len = dir.length() || 0.001;
  const g = new THREE.CylinderGeometry(r1, r0, len, segs, 1, true).toNonIndexed();
  g.translate(0, len / 2, 0);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(LIMB_A, dir.divideScalar(len)));
  g.translate(from[0], from[1], from[2]);
  return g;
}

// ---------------------------------------------------------------------------
// CLOTH — banners, flags and pennants, on every map.
//
// Two problems solved together. (1) A hanging cloth is the cheapest thing that
// makes a wall read as a BUILT place rather than a face of a cube: it breaks
// the silhouette, it gives the eye a scale reference, and it is the one piece
// of map dressing that is allowed to MOVE. (2) The only cloth in the game was
// six flat 0.1 m BOXES on medieval_castle — a banner drawn as a slab, which is
// exactly the "stacked cubes" read this mission exists to kill.
//
// Cost contract: every sheet on a map is baked into ONE merged geometry with
// ONE `cloth` material, and every pole into one more with a metal material —
// +2 draw calls per map, no per-frame CPU (the sway is `wind.ts`, one uniform).
// ---------------------------------------------------------------------------

/** Segment counts. The fold ripple needs columns and the sway needs rows; 7×9
 *  is the point where doubling either stopped being visible on a 3.6 m banner
 *  (=144 tris per sheet, ~3.2k for a whole map's worth). */
export const CLOTH_SEG_X = 7, CLOTH_SEG_Y = 9;
/** Metres of world per texture repeat — an ~8 px thread in the 512 px `cloth`
 *  albedo lands at ~9 mm, i.e. coarse canvas. Too fine and the weave aliases
 *  into grey; too coarse and a banner looks like sackcloth. */
export const CLOTH_TILE = 0.6;

/**
 * One hanging sheet, in local space, with the anchor edge at the origin.
 *
 *  - `"top"`   — a BANNER: hung from a pole along its top edge, spanning
 *                x ∈ [-w/2, w/2], y ∈ [-h, 0]. Its bottom is cut into a
 *                swallowtail (the middle of the bottom row is raised), which is
 *                most of what makes it read as heraldry rather than as a towel.
 *  - `"left"`  — a FLAG: mounted on a mast along x = 0, spanning x ∈ [0, w].
 *                The ripple amplitude grows toward the fly edge.
 *
 * Folds are a standing sine across the sheet, deepening away from the anchor —
 * a real hanging cloth is stiff at its hem and loose at its free edge. The
 * normal map does the thread detail; this does the DRAPE, and drape is what a
 * flat quad can never fake.
 */
export function clothSheet(
  w: number, h: number, anchor: "top" | "left", seed: number,
): THREE.BufferGeometry {
  const geo = new THREE.PlaneGeometry(w, h, CLOTH_SEG_X, CLOTH_SEG_Y);
  geo.translate(anchor === "top" ? 0 : w / 2, anchor === "top" ? -h / 2 : 0, 0);
  const pos = geo.attributes.position;
  const uv = geo.attributes.uv;
  const folds = 2 + (seed % 3); // 2..4 standing folds — never the same twice
  const phase = (seed % 7) * 0.9;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i);
    // `free` = 0 at the anchored edge, 1 at the loose one
    const free = anchor === "top" ? -y / h : x / w;
    const across = anchor === "top" ? (x / w + 0.5) : (y / h + 0.5);
    const amp = 0.055 * w * (0.28 + 0.72 * free);
    pos.setZ(i, Math.sin(across * Math.PI * folds + phase) * amp);
    // swallowtail: raise the middle of a banner's bottom row
    const cut = anchor === "top" && free > 0.999 ? h * 0.16 * (1 - Math.abs(x / (w / 2))) : 0;
    if (cut) pos.setY(i, y + cut);
    // UV from the FINAL position, not the pre-cut one: the weave has to run
    // continuously into the swallowtail, and reading `y` here before the cut
    // sheared the threads by the full notch depth on that one row.
    uv.setXY(i, x / CLOTH_TILE, (y + cut) / CLOTH_TILE); // world-scale, never stretched
  }
  pos.needsUpdate = true; uv.needsUpdate = true;
  geo.computeVertexNormals();
  // Sway weight: quadratic away from the anchor, with the outer edges of a
  // banner a touch looser than its middle (a hem sags in the centre).
  return windGeometry(geo, (x, y) => {
    const free = anchor === "top" ? -y / h : x / w;
    const edge = anchor === "top" ? 0.78 + 0.22 * Math.abs(x / (w / 2)) : 1;
    return free * free * edge;
  });
}

/** Collects every sheet and pole on a map, then emits the two merged meshes.
 *  Sheets are baked to world space here (not parented) so the merge is exact
 *  and the wind phase — which reads world position — is continuous across the
 *  whole map instead of restarting per banner. */
class ClothSet {
  private sheets: THREE.BufferGeometry[] = [];
  private poles: THREE.BufferGeometry[] = [];
  private m4 = new THREE.Matrix4();
  private euler = new THREE.Euler();
  private seed = 1;

  /** Hang one banner/flag with its anchor edge at (x,y,z), facing `yaw`. */
  add(
    w: number, h: number, anchor: "top" | "left",
    x: number, y: number, z: number, yaw: number, hex: string,
    pole = true,
  ): void {
    const geo = clothSheet(w, h, anchor, this.seed++);
    paintFlat(geo, hex);
    this.m4.makeRotationFromEuler(this.euler.set(0, yaw, 0));
    this.m4.setPosition(x, y, z);
    geo.applyMatrix4(this.m4);
    this.sheets.push(geo);
    if (!pole) return;
    // the rod the cloth hangs off — rigid, so it lives in the other mesh
    const rod = anchor === "top"
      ? new THREE.CylinderGeometry(0.045, 0.045, w + 0.34, 6).rotateZ(Math.PI / 2)
      : new THREE.CylinderGeometry(0.05, 0.05, h * 1.5, 6).translate(0, h * 0.35, 0);
    rod.applyMatrix4(this.m4);
    this.poles.push(rod);
  }

  /** Merge and attach. Returns the number of sheets (0 = nothing added). */
  build(g: THREE.Group): number {
    if (!this.sheets.length) return 0;
    const sheet = mergeGeometries(this.sheets, false);
    for (const s of this.sheets) s.dispose();
    if (sheet) {
      const mat = applyWind(new THREE.MeshStandardMaterial(surfaceMaps("cloth", {
        vertexColors: true, roughness: 0.94, metalness: 0.02, side: THREE.DoubleSide,
      }, 0.85)), 0.15, 1.45);
      const m = new THREE.Mesh(sheet, mat);
      // castShadow stays FALSE: the depth pass would not run the sway (see
      // wind.ts), so a swaying banner would drag a rigid shadow.
      m.receiveShadow = false;
      m.frustumCulled = false; // one mesh spans the whole map
      g.add(m);
    }
    const poles = this.poles.length ? mergeGeometries(this.poles, false) : null;
    for (const p of this.poles) p.dispose();
    if (poles) {
      g.add(new THREE.Mesh(poles, new THREE.MeshStandardMaterial(surfaceMaps("surface", {
        color: "#2a2a2e", roughness: 0.55, metalness: 0.6,
      }, 0.6))));
    }
    const n = this.sheets.length;
    this.sheets = []; this.poles = [];
    return n;
  }
}

/** Flat per-geometry vertex colour — the cloth mesh is ONE material, so the
 *  colour of an individual banner has to travel in the vertex buffer. */
function paintFlat(geo: THREE.BufferGeometry, hex: string): void {
  const col = new THREE.Color(hex);
  const n = geo.getAttribute("position").count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { arr[i * 3] = col.r; arr[i * 3 + 1] = col.g; arr[i * 3 + 2] = col.b; }
  geo.setAttribute("color", new THREE.BufferAttribute(arr, 3));
}

/** Max sheets from the GENERIC pass on one map. A cap, not a target — dense
 *  maps (medieval_castle has 90+ candidate walls) would otherwise bury the
 *  place in bunting. */
export const BANNERS_PER_MAP = 22;

/**
 * The generic pass: hang banners on the tall walls of ANY map, chosen from the
 * map's own boxes — so a map that does not exist yet gets them for free, the
 * same way `previews.ts` renders any MapDef and `dressStructure` trims any
 * building.
 *
 * A candidate is a wall tall enough to be a facade (the `facadeLike` shape
 * test, restated here because decor must not depend on the dressing module) and
 * whose top is within reach of the eye. Banners go on the side facing the map
 * CENTRE: the play space is inside, and a banner on the outer face of the outer
 * wall is a banner nobody will ever see.
 *
 * Pure except for the two arguments it fills — exported for the offline bench.
 */
export function bannerSpots(map: MapDef, rnd: () => number): {
  w: number; h: number; x: number; y: number; z: number; yaw: number;
}[] {
  const { min, max } = map.bounds;
  const cx = (min[0] + max[0]) / 2, cz = (min[2] + max[2]) / 2;
  const out: { w: number; h: number; x: number; y: number; z: number; yaw: number }[] = [];
  const walls = map.boxes.filter((b) => {
    const [w, h, d] = b.s;
    const top = b.p[1] + h / 2, bot = b.p[1] - h / 2;
    return h >= 4.5 && Math.max(w, d) >= 6 && Math.min(w, d) >= 0.9
      && top >= 4 && top <= 44 && bot <= 4;
  });
  for (const b of walls) {
    if (out.length >= BANNERS_PER_MAP) break;
    const [sw, sh, sd] = b.s;
    const [bx, by, bz] = b.p;
    // which of the four faces looks at the middle of the map?
    const dx = cx - bx, dz = cz - bz;
    const useX = Math.abs(dx) * sd > Math.abs(dz) * sw; // pick the face that is both facing in AND wide
    const nx = useX ? Math.sign(dx) || 1 : 0;
    const nz = useX ? 0 : Math.sign(dz) || 1;
    const faceLen = useX ? sd : sw;
    const half = (useX ? sw : sd) / 2;
    const top = by + sh / 2, base = by - sh / 2;
    // A short shopfront gets a short banner: a 3.6 m sheet on a 5 m wall is a
    // curtain. Height first, then the width that keeps heraldic proportions.
    const bh = Math.max(1.8, Math.min(3.6, sh * 0.45));
    const bw = bh * 0.49;
    // Pole height: just under the roof, but never higher than ~9.5 m off the
    // wall's base (a banner 30 m up a tower is a smudge) — and never so low
    // that the sheet's bottom would hang BELOW the wall it is nailed to, which
    // is how a banner ends up dangling into a stairwell or through a floor.
    // The three clamps cannot conflict: the shape test already demands h >= 7.
    const py = Math.max(Math.min(top - 1.0, base + 9.5), base + bh + 0.4);
    const count = Math.max(1, Math.min(3, Math.floor(faceLen / 7)));
    for (let i = 0; i < count; i++) {
      if (out.length >= BANNERS_PER_MAP) break;
      const t = (i + 1) / (count + 1) - 0.5;              // spread along the face
      const along = t * (faceLen - bw - 1.2) + (rnd() - 0.5) * 0.6;
      const off = half + 0.16;                            // clear of the wall
      const x = bx + (useX ? nx * off : along);
      const z = bz + (useX ? along : nz * off);
      // Reject a spot swallowed by a NEIGHBOURING box — walls butt against each
      // other and against floors all over these maps, and a banner hung into
      // the next tower is invisible from every angle. Cheap: this runs once at
      // map load, never per frame.
      if (map.boxes.some((o) =>
        Math.abs(x - o.p[0]) < o.s[0] / 2 - 0.02 &&
        Math.abs(z - o.p[2]) < o.s[2] / 2 - 0.02 &&
        // either the anchor or the free hem inside another box is enough
        ((py < o.p[1] + o.s[1] / 2 - 0.02 && py > o.p[1] - o.s[1] / 2 + 0.02) ||
         (py - bh < o.p[1] + o.s[1] / 2 - 0.02 && py - bh > o.p[1] - o.s[1] / 2 + 0.02)))) continue;
      out.push({ w: bw, h: bh, x, y: py, z, yaw: Math.atan2(nx, nz) });
    }
  }
  return out;
}

/** One prop = a list of (geometry, material) parts that all ride the SAME
 *  transform. A tree is bark + canopy and a bollard is post + emissive head:
 *  a merged single-material prop can only ever wear ONE texture, and a trunk
 *  painted with a leaf texture is worse than no texture at all. Each part is
 *  one InstancedMesh for the whole map, so the split costs exactly +1 draw
 *  call per split prop. */
export type PropParts = [THREE.BufferGeometry, THREE.Material][];

/** Broadleaf tree frame (shared with mapProps.ts, which hangs real-leaf card
 *  clusters where these crown blobs are — the trunk/branches stay). */
const LEAN_X = 0.13, LEAN_Z = -0.08, BOLE_H = 2.05;
// crown blobs sit around where the BENT bole actually ends, not around 0
export const TREE_CROWN_BLOBS: [number, number, number, number, string][] = [
  [LEAN_X, 2.0, LEAN_Z, 1.02, "#4a7639"], [LEAN_X + 0.6, 2.42, LEAN_Z + 0.18, 0.72, "#578a44"],
  [LEAN_X - 0.55, 2.24, LEAN_Z - 0.3, 0.7, "#426a34"], [LEAN_X + 0.1, 2.82, LEAN_Z - 0.06, 0.6, "#619b4d"],
  [LEAN_X - 0.18, 2.55, LEAN_Z + 0.5, 0.56, "#4f7a40"], [LEAN_X + 0.42, 2.06, LEAN_Z - 0.52, 0.5, "#457036"],
  // two lower lobes that DROOP down over the visible branches, so the crown
  // skirts into the bole instead of floating as a ball above it (the last
  // remaining "lollipop" read). Kept inside the existing footprint radius.
  [LEAN_X + 0.5, 1.66, LEAN_Z + 0.34, 0.5, "#3f6533"], [LEAN_X - 0.44, 1.6, LEAN_Z - 0.42, 0.46, "#4d7a3d"],
  // PERIPHERAL TUFTS — smaller lumps pushed OUT and UP past the six-lobe core so
  // the crown edge is notched and gappy (sky shows THROUGH it) instead of the
  // smooth round envelope that read as a low-poly ball. Kept inside the old
  // outer reach (0.6+0.72 ≈ 1.32) so the footprint — and clip risk — is unchanged.
  [LEAN_X + 0.86, 2.32, LEAN_Z + 0.48, 0.4, "#548540"], [LEAN_X - 0.82, 2.5, LEAN_Z + 0.16, 0.42, "#446e37"],
  [LEAN_X + 0.22, 3.18, LEAN_Z + 0.28, 0.44, "#5c9448"], [LEAN_X - 0.3, 2.94, LEAN_Z - 0.56, 0.4, "#426a34"],
  [LEAN_X + 0.72, 2.74, LEAN_Z - 0.42, 0.38, "#4f7a40"],
];
const blobSpec = TREE_CROWN_BLOBS;
/** Conifer frame (shared with mapProps.ts's branch-card crown). */
export const PINE_LEAN_X = -0.06, PINE_LEAN_Z = 0.05, PINE_BOLE = 4.35;
/** Sway curve shared by every upright plant part (see plantSway). */
export const PLANT_BASE = 0.25, PLANT_TOP = 2.3;

/** Every natural-decor prop in the game, built once per map load from `rnd`.
 *
 *  Extracted from `buildDecor` so the screenshot harness can park one of each
 *  in front of the camera (match.ts DECOR_DEMO) — the props are scattered over
 *  a map's border band at 0.25–1.5 scale, which is exactly the thing a headless
 *  run that cannot walk or aim can never photograph. Pure: no scene, no side
 *  effects, deterministic in `rnd`. */
export function naturalProps(rnd: () => number): Record<
  "rock" | "bush" | "tree" | "pine" | "kelp" | "darkRock" | "bollard" | "grass", PropParts
> {
  // DAPPLED paint: each vertex gets a small random hue/luminance jitter so a
  // canopy reads as thousands of leaves catching light rather than one flat
  // surface. It MULTIPLIES with the `canopy`/`bark`/`boulder` textures (three
  // folds vertexColors into map), so the two are complementary: the texture
  // supplies the fine detail, the paint the per-blob and per-facet tonal
  // variation a single tiling texture cannot.
  const paint = (geo: THREE.BufferGeometry, hex: string, jit = 0): THREE.BufferGeometry => {
    const base = new THREE.Color(hex);
    const n = geo.getAttribute("position").count;
    const arr = new Float32Array(n * 3);
    const hsl = { h: 0, s: 0, l: 0 };
    base.getHSL(hsl);
    for (let i = 0; i < n; i++) {
      if (jit > 0) {
        const c = new THREE.Color().setHSL(
          hsl.h + (rnd() - 0.5) * 0.05,
          Math.min(1, hsl.s + (rnd() - 0.5) * 0.15),
          Math.max(0, Math.min(1, hsl.l + (rnd() - 0.5) * jit)),
        );
        arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b;
      } else { arr[i * 3] = base.r; arr[i * 3 + 1] = base.g; arr[i * 3 + 2] = base.b; }
    }
    geo.setAttribute("color", new THREE.BufferAttribute(arr, 3));
    return geo;
  };

  /** An irregular boulder: a roughened icosahedron squashed on two axes so it
   *  has a long and a short face, then re-UV'd for the `boulder` granite. */
  const boulderGeo = (seed: number, amp: number): THREE.BufferGeometry => {
    const b = new THREE.IcosahedronGeometry(1, 1);
    roughen(b, amp, seed);
    b.scale(1, 0.78 + (seed % 3) * 0.12, 0.72 + (seed % 5) * 0.09);
    worldProjectUV(b, 0.7);
    return b;
  };
  /** Leaf material. `amp`/`freq` are the WIND (wind.ts): a canopy that never
   *  moves is the next thing that reads as fake once it has a silhouette, and
   *  the sway is a vertex program — the crowns stay one InstancedMesh per map
   *  and the per-frame cost stays one uniform write for the whole world. */
  const leafMat = (rough: number, scale: number, amp: number, freq = 1): THREE.Material =>
    applyWind(new THREE.MeshStandardMaterial(surfaceMaps("canopy", {
      vertexColors: true, roughness: rough, metalness: 0, flatShading: true,
    }, scale)), amp, freq);

  /** The ONE sway curve every upright plant shares. The trunk and the crown are
   *  separate meshes with separate materials; if their weights disagreed at the
   *  bole top the crown would slide off it, so both call this. */
  const plantSway = (g2: THREE.BufferGeometry): THREE.BufferGeometry =>
    windGeometry(g2, (_x, y) => uprightSway(y, PLANT_BASE, PLANT_TOP));

  // --- BUSH: three overlapping roughened lobes rather than one sphere. A lone
  // icosahedron is a ball whatever you paint on it; the lobes are what make the
  // silhouette read as a shrub from any angle.
  // Six lobes of varied size, pushed off-centre and heavily roughened so the
  // shrub reads as a clumped mass with a notched, gappy edge — the old three
  // near-round lobes packed into a bright-green faceted BALL, the single most
  // toy element on every day/neon map's ground. The greens are pulled toward a
  // muted natural olive (the old #7fab60 read as plastic on desert sand), and
  // the larger lobes get finer facets so they stop looking low-poly up close.
  const bushLobes = ([
    [0, 0, 0, 1.0, "#57784a"], [0.6, 0.16, 0.22, 0.62, "#657f4c"],
    [-0.5, 0.08, -0.34, 0.56, "#4c6a3d"], [0.3, 0.36, -0.38, 0.5, "#5e7847"],
    [-0.4, 0.32, 0.36, 0.46, "#516e42"], [0.14, 0.5, 0.12, 0.44, "#3f5a34"],
  ] as const)
    .map(([bx, by, bz, r, hex], i) => {
      const b = new THREE.IcosahedronGeometry(r, r >= 0.55 ? 2 : 1);
      roughen(b, 0.26, 11 + i * 7);
      b.translate(bx, by, bz);
      worldProjectUV(b, 0.42);
      windGeometry(b, (_x, y) => uprightSway(y, -0.7, 1.0));
      return paint(b, hex, 0.2);
    });

  /** Tag a part so mapProps.ts swaps it for the real-asset version. */
  const tagged = (geo: THREE.BufferGeometry, kind: string): THREE.BufferGeometry => {
    geo.userData.propKind = kind;
    return geo;
  };

  // --- TREE. The silhouette is the real change: a ROOT FLARE at the base, a
  // BEND so the bole leans, and three BRANCHES climbing into the crown. The old
  // tree was a straight dowel under a ball of blobs, i.e. a lollipop.
  const flare = new THREE.CylinderGeometry(0.2, 0.42, 0.36, 7).toNonIndexed();
  flare.translate(0, 0.18, 0);
  const bole = new THREE.CylinderGeometry(0.115, 0.2, BOLE_H - 0.3, 7).toNonIndexed();
  bole.translate(0, 0.3 + (BOLE_H - 0.3) / 2, 0);
  const blobs = blobSpec.map(([bx, by, bz, r, hex], i) => {
    const b = new THREE.IcosahedronGeometry(r, r >= 0.5 ? 2 : 1);
    roughen(b, 0.22, 31 + i * 13);
    b.translate(bx, by, bz);
    worldProjectUV(b, 0.55);
    plantSway(b);
    return paint(b, hex, 0.16);
  });
  // branches reach from the bole out to the outer crown blobs — including the
  // two drooping lower lobes (6,7), so a limb is visibly carrying the skirt.
  const branches = ([[1, 0.35], [2, 0.55], [5, 0.28], [6, 0.22], [7, 0.2]] as const).map(([bi, at]) => {
    const [bx, by, bz] = blobSpec[bi];
    return limb(0.075, 0.028,
      [LEAN_X * at ** 1.5, 0.55 + at * 0.9, LEAN_Z * at ** 1.5],
      [bx * 0.85, by * 0.94, bz * 0.85]);
  });
  const trunkGeo = bend(mergeGeometries([flare, bole, ...branches])!, LEAN_X, LEAN_Z, BOLE_H);
  worldProjectUV(trunkGeo, 0.5);
  plantSway(trunkGeo);
  paint(trunkGeo, "#7a5638", 0.1);

  // --- CONIFER (pine/spruce). A SECOND species so a day map's tree line stops
  // being 26 clones of one broadleaf — mixed silhouettes is the single strongest
  // "this is a real landscape" cue. A spire, not a ball: a tall tapered bole and
  // four stacked, roughened, drooping foliage TIERS narrowing to a tip, wearing
  // the dark blue-green `needle` texture. Footprint is NARROWER than the
  // deciduous crown, so it slots into the same scatter without new clip risk.
  const pineFlare = new THREE.CylinderGeometry(0.13, 0.28, 0.34, 7).toNonIndexed();
  pineFlare.translate(0, 0.17, 0);
  const pineBole = new THREE.CylinderGeometry(0.06, 0.13, PINE_BOLE - 0.3, 7).toNonIndexed();
  pineBole.translate(0, 0.3 + (PINE_BOLE - 0.3) / 2, 0);
  const pineTrunk = bend(mergeGeometries([pineFlare, pineBole])!, PINE_LEAN_X, PINE_LEAN_Z, PINE_BOLE);
  worldProjectUV(pineTrunk, 0.5);
  plantSway(pineTrunk);
  paint(pineTrunk, "#6a4a30", 0.1);
  // four skirts: each a cone with a WIDE base and slight downward droop at the
  // rim (roughen makes the edge ragged, not a clean bell). Higher tiers lean
  // with the bole so the spire follows the trunk's bend.
  const pineTiers = ([
    [0.95, 1.55, 0.95, "#2f5738"], [0.74, 1.35, 1.85, "#356237"],
    [0.52, 1.15, 2.75, "#2c5233"], [0.3, 0.95, 3.6, "#3a6b3d"],
  ] as const).map(([rad, h, y, hex], i) => {
    const cone = new THREE.ConeGeometry(rad, h, 9).toNonIndexed();
    roughen(cone, 0.12, 53 + i * 17);
    cone.translate(PINE_LEAN_X * (y / PINE_BOLE) ** 1.5, y + h / 2, PINE_LEAN_Z * (y / PINE_BOLE) ** 1.5);
    worldProjectUV(cone, 0.6);
    plantSway(cone);
    return paint(cone, hex, 0.14);
  });
  const pineFoliage = tagged(mergeGeometries(pineTiers)!, "pineCrown");

  // --- KELP: each blade CURVES along its height (`bend`) instead of tilting
  // rigidly, and the tallest stipe carries a flattened frond at its tip, so the
  // cluster reads as kelp drifting rather than as three cones.
  const blade = (dx: number, dz: number, h: number, hex: string): THREE.BufferGeometry => {
    const b = new THREE.CylinderGeometry(0.035, 0.15, h, 5).toNonIndexed();
    b.translate(0, h / 2, 0);
    bend(b, dx, dz, h);
    worldProjectUV(b, 0.5);
    windGeometry(b, (_x, y) => uprightSway(y, 0, 3.0));
    return paint(b, hex, 0.14);
  };
  const frond = new THREE.IcosahedronGeometry(0.42, 1);
  roughen(frond, 0.22, 71);
  frond.scale(1.5, 0.9, 0.28);
  frond.translate(0.5, 3.2, 0.22);
  worldProjectUV(frond, 0.4);
  windGeometry(frond, (_x, y) => uprightSway(y, 0, 3.0));

  // --- BOLLARD. Was one emissive cylinder — a glowing stick, so the whole post
  // bloomed and it read as a neon pole rather than as a light. Now a dark
  // machined POST with a base plate and a collar on an ordinary lit material,
  // so it takes the scene's shading like real street furniture, plus a separate
  // emissive HEAD: the glow is a point on top of a silhouette instead of being
  // the entire silhouette.
  const ring = (r0: number, r1: number, h: number, y: number, seg: number): THREE.BufferGeometry => {
    const b = new THREE.CylinderGeometry(r0, r1, h, seg);
    b.translate(0, y, 0);
    return b;
  };
  const post = mergeGeometries([
    ring(0.17, 0.2, 0.055, 0.027, 10), ring(0.062, 0.082, 0.86, 0.485, 8), ring(0.095, 0.095, 0.05, 0.7, 10),
  ])!;
  worldProjectUV(post, 0.35);

  // --- GRASS TUFT: real 3D ground cover for the natural-ground family. The new
  // `terrain` texture gives the field micro-relief, but a texture is still a
  // flat sheet under a grazing camera — a scatter of short tufts gives the
  // ground an actual SILHOUETTE and MOTION. Each tuft is a fan of tapered blades
  // that ARC over (`bend`) and SWAY from a fixed root (windGeometry), shaded
  // dark root -> bright tip in vertex colour. buildDecor scatters the whole lawn
  // as ONE InstancedMesh, so it is a single draw call and the sway is the shared
  // wind vertex program — no per-frame CPU, no per-frame allocation.
  const grassBlade = (yaw: number, lean: number, h: number, hex0: string, hex1: string): THREE.BufferGeometry => {
    const b = new THREE.CylinderGeometry(0.004, 0.03, h, 3).toNonIndexed();
    b.translate(0, h / 2, 0);
    bend(b, lean, 0, h);   // arc over toward +x...
    b.rotateY(yaw);        // ...then aim the whole blade at its own heading
    const c0 = new THREE.Color(hex0), c1 = new THREE.Color(hex1);
    const bp = b.getAttribute("position");
    const arr = new Float32Array(bp.count * 3);
    for (let i = 0; i < bp.count; i++) {
      const t = Math.max(0, Math.min(1, bp.getY(i) / h));
      arr[i * 3] = c0.r + (c1.r - c0.r) * t;
      arr[i * 3 + 1] = c0.g + (c1.g - c0.g) * t;
      arr[i * 3 + 2] = c0.b + (c1.b - c0.b) * t;
    }
    b.setAttribute("color", new THREE.BufferAttribute(arr, 3));
    windGeometry(b, (_x, y) => uprightSway(y, 0, h));
    return b;
  };
  const grassBlades: THREE.BufferGeometry[] = [];
  const TUFT_N = 7;
  for (let i = 0; i < TUFT_N; i++) {
    const yaw = (i / TUFT_N) * Math.PI * 2 + rnd() * 0.7;
    const h = 0.34 + rnd() * 0.26;
    const dry = rnd() < 0.4;
    grassBlades.push(grassBlade(yaw, 0.14 + rnd() * 0.14, h,
      dry ? "#556b32" : "#3c5a26", dry ? "#b7bd6a" : "#8fae52"));
  }
  const grassTuft = mergeGeometries(grassBlades)!;

  return {
    rock: [[paint(boulderGeo(3, 0.3), "#a09a8c", 0.1),
      new THREE.MeshStandardMaterial(surfaceMaps("boulder", {
        vertexColors: true, roughness: 1, metalness: 0, flatShading: true,
      }, 1.0))]],
    darkRock: [[paint(boulderGeo(8, 0.34), "#2b3145", 0.12),
      new THREE.MeshStandardMaterial(surfaceMaps("boulder", {
        vertexColors: true, roughness: 0.95, metalness: 0.05, flatShading: true,
      }, 1.0))]],
    bush: [[mergeGeometries(bushLobes)!, leafMat(1, 1.0, 0.05, 1.35)]],
    tree: [
      [trunkGeo, applyWind(new THREE.MeshStandardMaterial(surfaceMaps("bark", {
        vertexColors: true, roughness: 1, metalness: 0,
      }, 1.15)), 0.17, 0.9)],
      [tagged(mergeGeometries(blobs)!, "treeCrown"), leafMat(1, 1.0, 0.17, 0.9)],
    ],
    pine: [
      [pineTrunk, applyWind(new THREE.MeshStandardMaterial(surfaceMaps("bark", {
        vertexColors: true, roughness: 1, metalness: 0,
      }, 1.0)), 0.1, 1.0)],
      // needle foliage: its own family, swaying a touch stiffer than a broadleaf
      [pineFoliage, applyWind(new THREE.MeshStandardMaterial(surfaceMaps("needle", {
        vertexColors: true, roughness: 1, metalness: 0, flatShading: true,
      }, 0.85)), 0.11, 1.05)],
    ],
    kelp: [[mergeGeometries([
      blade(0.5, 0.14, 3.0, "#2f7d4a"), blade(-0.62, -0.3, 2.4, "#3a9256"),
      blade(0.16, -0.55, 3.5, "#256b3d"), paint(frond, "#2b8a55", 0.14),
    ])!, leafMat(0.85, 0.7, 0.30, 0.45)]],
    bollard: [
      [post, new THREE.MeshStandardMaterial(surfaceMaps("surface", {
        color: "#1c2033", roughness: 0.45, metalness: 0.65,
      }, 0.8))],
      [ring(0.085, 0.07, 0.12, 0.975, 10), new THREE.MeshStandardMaterial({
        color: "#2a1030", emissive: "#ff2fd0", emissiveIntensity: 2.1, roughness: 0.5,
      })],
    ],
    grass: [[grassTuft, applyWind(new THREE.MeshStandardMaterial({
      vertexColors: true, roughness: 0.92, metalness: 0, flatShading: true,
    }), 0.08, 1.9)]],
  };
}

/** One scattered prop's footprint: where it meets the ground, the blob radius
 *  its silhouette wants, and how dark that blob should be. */
export interface DecorGroundSpot { x: number; y: number; z: number; r: number; s: number }

/** Deterministic 2D gradient noise in [-1, 1] (Perlin-style, hashed lattice
 *  gradients, quintic fade). Seeded so every map gets its own range. */
function gradNoise2(seed: number): (x: number, y: number) => number {
  const grad = (ix: number, iy: number): [number, number] => {
    const h = Math.sin(ix * 127.1 + iy * 311.7 + seed * 74.7) * 43758.5453;
    const a = (h - Math.floor(h)) * Math.PI * 2;
    return [Math.cos(a), Math.sin(a)];
  };
  const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
  return (x, y) => {
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const fx = x - x0, fy = y - y0;
    const dot = (ix: number, iy: number): number => {
      const g = grad(x0 + ix, y0 + iy);
      return g[0] * (fx - ix) + g[1] * (fy - iy);
    };
    const u = fade(fx), v = fade(fy);
    const a = dot(0, 0) + (dot(1, 0) - dot(0, 0)) * u;
    const b = dot(0, 1) + (dot(1, 1) - dot(0, 1)) * u;
    return (a + (b - a) * v) * 1.41;
  };
}

/** The day-map horizon: one heightfield annulus (see the call site). */
function buildTerrainRange(g: THREE.Group, cx: number, cz: number, R: number, rnd: () => number): void {
  const noise = gradNoise2(Math.floor(rnd() * 1000));
  const warp = gradNoise2(Math.floor(rnd() * 1000) + 1);
  const big = gradNoise2(Math.floor(rnd() * 1000) + 2);
  // just past the map → deep in the fog. Capped inside the camera's 600 m far
  // plane: on the biggest maps (R ≈ 340) the rim would otherwise be clipped
  const r0 = R * 0.8, r1 = Math.max(r0 + 120, Math.min(R * 2.25, 580));
  const AS = 384, RS = 56;             // angular × radial segments
  const pos = new Float32Array((AS + 1) * (RS + 1) * 3);
  const uv = new Float32Array((AS + 1) * (RS + 1) * 2);
  const sm = (a: number, b: number, t: number): number => {
    const k = Math.max(0, Math.min(1, (t - a) / (b - a)));
    return k * k * (3 - 2 * k);
  };
  // ridged multifractal: 1-|n| makes sharp crests; each octave is weighted by
  // the previous one so detail piles onto the ridges, not the valley floors
  const ridged = (x: number, z: number): number => {
    let sum = 0, amp = 0.55, freq = 1 / 125, prev = 1;
    for (let o = 0; o < 6; o++) {
      let n = 1 - Math.abs(noise(x * freq, z * freq));
      n = n * (0.35 + 0.65 * n); // softer than n² — rounded crests, not needles
      sum += n * amp * prev;
      prev = Math.min(1, n * 1.6);
      amp *= 0.5;
      freq *= 2.03;
    }
    return sum; // ~0..1
  };
  let maxH = 1;
  for (let j = 0; j <= RS; j++) {
    const t = j / RS;
    // radial spacing biased toward the inner edge, where the eye is closest
    const r = r0 + (r1 - r0) * (t * 0.55 + t * t * 0.45);
    for (let i = 0; i <= AS; i++) {
      const a = (i / AS) * Math.PI * 2;
      const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
      // domain warp: bends the ridgelines so they don't all run radially
      const wx = x + 38 * warp(x / 160, z / 160), wz = z + 38 * warp(x / 160 + 9.1, z / 160 - 4.3);
      // envelope: foothills rise off the map edge, massifs at the back, a
      // slow along-the-ring swell so some bearings are taller than others
      // low rolling foothills for the first third (they sit right behind the
      // map wall — tall ones there wall the map in like a canyon), the real
      // massifs only past the middle, where distance + haze sell their size
      const rise = sm(0, 0.22, t);
      const scale = 9 + 26 * sm(0.05, 0.45, t) + 52 * sm(0.4, 0.95, t);
      const swell = 0.7 + 0.5 * (big(x / 420, z / 420) * 0.5 + 0.5);
      const back = 1 - 0.35 * sm(0.9, 1, t); // drop a little at the very rim
      const h = rise * back * swell * scale * (0.18 + 1.05 * ridged(wx, wz)) - 4;
      const k = (j * (AS + 1) + i) * 3;
      pos[k] = x; pos[k + 1] = h; pos[k + 2] = z;
      if (h > maxH) maxH = h;
      const q = (j * (AS + 1) + i) * 2;
      uv[q] = x / 14; uv[q + 1] = z / 14; // top-down: no axis-switch seams
    }
  }
  const idx: number[] = [];
  for (let j = 0; j < RS; j++) {
    for (let i = 0; i < AS; i++) {
      const a = j * (AS + 1) + i, b = a + 1, c = a + (AS + 1), d = c + 1;
      // CCW seen from above (i runs counter-clockwise, j outward) → normals up
      idx.push(a, b, c, b, d, c);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  // ---- colour from slope + altitude
  const nor = geo.getAttribute("normal");
  const col = new Float32Array(pos.length);
  const FOREST = new THREE.Color("#2f4527"), GRASS = new THREE.Color("#5c7a43");
  const ROCK = new THREE.Color("#6e706a"), ROCK_HI = new THREE.Color("#8d918f");
  const SNOW = new THREE.Color("#eef3f8");
  const HAZE = new THREE.Color("#a9bccb");
  const c = new THREE.Color(), tmp = new THREE.Color();
  const snowLine = maxH * 0.62;
  for (let v = 0; v < pos.length / 3; v++) {
    const h = pos[v * 3 + 1];
    const ny = nor.getY(v);                    // 1 = flat, 0 = vertical
    const alt = Math.max(0, h) / maxH;
    const jitter = noise(pos[v * 3] / 9, pos[v * 3 + 2] / 9) * 0.5 + 0.5;
    // vegetation: forest low, thinning to grass, gone above ~55% height
    c.copy(FOREST).lerp(GRASS, Math.min(1, alt * 1.8 + jitter * 0.25));
    // rock where steep OR high (tree line)
    const rockAmt = Math.max(sm(0.86, 0.68, ny), sm(0.38, 0.58, alt + (jitter - 0.5) * 0.12));
    tmp.copy(ROCK).lerp(ROCK_HI, alt);
    c.lerp(tmp, rockAmt);
    // snow above the snowline, only on faces flat enough to hold it
    const snow = sm(snowLine * 0.92, snowLine * 1.12, h + (jitter - 0.5) * maxH * 0.08) * sm(0.55, 0.8, ny);
    c.lerp(SNOW, snow);
    // AERIAL PERSPECTIVE: distant rock goes blue-grey and loses contrast
    // well before the linear fog would touch it — the main cue that reads a
    // ridge as kilometres away rather than a backdrop
    const dist = Math.hypot(pos[v * 3] - cx, pos[v * 3 + 2] - cz);
    c.lerp(HAZE, 0.55 * sm(r0, r1, dist));
    col[v * 3] = c.r; col[v * 3 + 1] = c.g; col[v * 3 + 2] = c.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  geo.computeBoundingSphere();
  const mesh = new THREE.Mesh(geo,
    new THREE.MeshStandardMaterial(surfaceMaps("hillside", { vertexColors: true, roughness: 1, metalness: 0 }, 0.9)));
  mesh.name = "terrainRange";
  g.add(mesh);
}

export function buildDecor(map: MapDef): THREE.Group {
  const g = new THREE.Group();
  const rnd = lcg(map.id);
  const theme = map.theme ?? "neon";
  const { min, max } = map.bounds;
  const spanX = max[0] - min[0];
  const spanZ = max[2] - min[2];
  const cx = (min[0] + max[0]) / 2;
  const cz = (min[2] + max[2]) / 2;

  // --- distant scenery ring: fogged silhouettes past the map edge ----------
  const R = Math.max(spanX, spanZ) * 0.85 + 55;
  const tex = mapTextures();
  if (map.id === "neo_island" || map.id === "rooftop_spiderweb") {
    // SKYHAVEN: the far mountains DANCE to the music — instanced so each one
    // squashes/stretches independently (each mapped to a random band, so some
    // rise while others flatten). Synthwave silhouette colour.
    const cone = new THREE.ConeGeometry(1, 1, 7);
    cone.translate(0, 0.5, 0); // base pivot → scale.y squashes from the ground up
    const mat = new THREE.MeshStandardMaterial({ color: "#3a1a52", roughness: 1, metalness: 0, emissive: "#ff2ec4", emissiveIntensity: 0.12 });
    const im = new THREE.InstancedMesh(cone, mat, 30);
    im.frustumCulled = false;
    const bases: { x: number; z: number; rad: number; h: number; band: number; sy: number }[] = [];
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion();
    for (let i = 0; i < 30; i++) {
      const a = (i / 30) * Math.PI * 2 + rnd() * 0.2;
      const r = R * (0.92 + rnd() * 0.35);
      const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
      const h = 14 + rnd() * 28, rad = 16 + rnd() * 26;
      m4.compose(new THREE.Vector3(x, -2, z), q, new THREE.Vector3(rad, h, rad));
      im.setMatrixAt(i, m4);
      bases.push({ x, z, rad, h, band: Math.floor(rnd() * EQ_BANDS), sy: h });
    }
    im.instanceMatrix.needsUpdate = true;
    im.userData.beatRing = bases;
    g.add(im);
  } else if (theme === "day") {
    // DISTANT MOUNTAINS (2026-11, map-realism mission): ONE continuous
    // heightfield range instead of 72 separate noisy cones. However much noise
    // a cone wears it still reads as a TENT — a lone conical silhouette with a
    // faceted 16-sided body — and a ring of them reads as a picket fence. Real
    // ranges are one connected surface: valleys between peaks, ridgelines that
    // run into each other, spurs, cols. So: a polar grid (annulus) from just
    // past the map to the far horizon, lifted by RIDGED fractal noise (sharp
    // crests, soft valleys — the erosion signature), with an envelope that
    // keeps the foothills low near the map and the big massifs at the back.
    // Smooth normals (it is one mesh), and colour from the GEOMETRY: forest on
    // gentle low slopes, grass higher up, bare rock wherever it is steep, snow
    // only above the snowline on faces flat enough to hold it. One draw call,
    // ~43k triangles, static, fogged, casts no shadow.
    buildTerrainRange(g, cx, cz, R, rnd);
  } else {
    // NEON CITY SKYLINE. Was 30 plain cubes wearing a window texture — the exact
    // "stacked Minecraft crates" the horizon read as. Now every silhouette is a
    // real building: a rectangular (not square) shaft that STEPS BACK in tiers
    // as it climbs, a dark mechanical penthouse on the roof, an antenna mast on
    // the tallest, and a red aircraft-warning BEACON at its tip. Footprints are
    // rotated off-axis and each tower is tinted a different brightness so the
    // ring stops reading as one building repeated. All merged/static, cast no
    // shadow, fade into the fog exactly as before — three draw calls for the
    // whole ring instead of one.
    const shaftGeos: THREE.BufferGeometry[] = []; // window facades
    const crownGeos: THREE.BufferGeometry[] = []; // dark rooftop plant + masts
    const beaconGeos: THREE.BufferGeometry[] = []; // emissive warning lights
    const tintShaft = (geo: THREE.BufferGeometry, mul: number): void => {
      // per-tower albedo variation. A merged mesh shares one material, so the
      // brightness spread lives in a vertex-colour attribute the material
      // multiplies its base colour by (lit windows stay in the emissive map, so
      // they are unaffected — only the dark wall between them varies).
      const p = geo.getAttribute("position");
      const col = new Float32Array(p.count * 3);
      col.fill(mul);
      geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    };
    for (let i = 0; i < 30; i++) {
      const a = (i / 30) * Math.PI * 2 + rnd() * 0.2;
      const r = R * (0.92 + rnd() * 0.35);
      const x = cx + Math.cos(a) * r;
      const z = cz + Math.sin(a) * r;
      const yaw = rnd() * Math.PI;              // break the axis-aligned grid
      let w = 9 + rnd() * 15, d = 9 + rnd() * 15; // rectangular, not square
      const h = 22 + rnd() * 55;
      const mul = 0.62 + rnd() * 0.6;           // 0.62..1.22 brightness spread
      const tiers = h > 62 ? 3 : h > 42 ? 2 : 1;
      let yTop = -2;                            // ring base sits 2 m below grade
      for (let t = 0; t < tiers; t++) {
        const th = (h / tiers) * (t === 0 ? 1.12 : 0.9);
        const bg = new THREE.BoxGeometry(w, th, d);
        worldTileUV(bg, w, th, d, 7);           // window grid tiles in world units
        tintShaft(bg, mul);
        bg.rotateY(yaw);
        bg.translate(x, yTop + th / 2, z);
        shaftGeos.push(bg);
        yTop += th;
        w *= 0.74; d *= 0.74;                   // setback for the tier above
      }
      // rooftop mechanical penthouse — dark, windowless
      const pw = Math.max(2.5, w * 0.9), pd = Math.max(2.5, d * 0.9), ph = 2 + rnd() * 4;
      const pent = new THREE.BoxGeometry(pw, ph, pd);
      pent.rotateY(yaw); pent.translate(x, yTop + ph / 2, z);
      crownGeos.push(pent);
      yTop += ph;
      // antenna mast + beacon on the taller towers of the skyline
      if (h > 40 || rnd() < 0.4) {
        const mh = 5 + rnd() * 12;
        const mast = new THREE.BoxGeometry(0.6, mh, 0.6);
        mast.translate(x, yTop + mh / 2, z);
        crownGeos.push(mast);
        const be = new THREE.BoxGeometry(1.4, 1.4, 1.4);
        be.translate(x, yTop + mh, z);
        beaconGeos.push(be);
      }
    }
    const shaftMat = new THREE.MeshStandardMaterial({
      color: "#141830", roughness: 0.85, metalness: 0.2,
      map: tex.facade.map,
      normalMap: tex.facade.normal ?? undefined,
      roughnessMap: tex.facade.roughness ?? undefined,
      emissiveMap: tex.facadeGlow,
      emissive: "#ffffff",
      emissiveIntensity: 0.75,
      vertexColors: true,
    });
    if (shaftMat.normalMap) shaftMat.normalScale.set(0.55, 0.55);
    g.add(new THREE.Mesh(mergeGeometries(shaftGeos), shaftMat));
    g.add(new THREE.Mesh(mergeGeometries(crownGeos),
      new THREE.MeshStandardMaterial({ color: "#0c0f1c", roughness: 0.9, metalness: 0.3 })));
    if (beaconGeos.length) {
      g.add(new THREE.Mesh(mergeGeometries(beaconGeos),
        new THREE.MeshStandardMaterial({ color: "#ff3a3a", emissive: "#ff2a2a", emissiveIntensity: 1.4, roughness: 1 })));
    }
  }

  // --- scattered ground props ----------------------------------------------
  // candidate surfaces: big ground-level slabs (never rooftops/islands).
  // Blue-dominant slabs are water (Zarya/Skyhaven oceans) — no props there.
  const notWater = (c?: string): boolean => {
    if (!c || c[0] !== "#") return true;
    const n = parseInt(c.slice(1), 16);
    const r = (n >> 16) & 255, gg = (n >> 8) & 255, b = n & 255;
    return !(b > r + 30 && b > gg + 10);
  };
  const grounds = map.boxes.filter(
    // top must be at/near the surface (>= -2): a SUBMERGED floor like the pirate
    // seabed (top ≈ -9) must NOT grow trees/bushes/rocks underwater — seaweed is
    // added separately below for that.
    (b) => b.s[0] >= 12 && b.s[2] >= 12 && b.p[1] + b.s[1] / 2 <= 2.5 && b.p[1] + b.s[1] / 2 >= -2 && notWater(b.color),
  );
  // never place decor INSIDE a building: reject spots with a roof overhead
  // (any box whose footprint covers the point with its underside 0.5–8 m up)
  const roofed = (x: number, y: number, z: number): boolean =>
    map.boxes.some((b) => {
      const bot = b.p[1] - b.s[1] / 2;
      return bot > y + 0.5 && bot < y + 8 &&
        Math.abs(x - b.p[0]) < b.s[0] / 2 && Math.abs(z - b.p[2]) < b.s[2] / 2;
    });
  const targetProps = Math.min(90, Math.max(24, Math.round((spanX * spanZ) / 130)));
  const pts: { x: number; y: number; z: number }[] = [];
  for (let i = 0; i < targetProps * 3 && pts.length < targetProps; i++) {
    const gb = grounds[Math.floor(rnd() * grounds.length)];
    if (!gb) break;
    const x = gb.p[0] + (rnd() - 0.5) * (gb.s[0] - 2);
    const z = gb.p[2] + (rnd() - 0.5) * (gb.s[2] - 2);
    const y = gb.p[1] + gb.s[1] / 2;
    if (roofed(x, y, z)) continue; // no rocks/bushes inside buildings
    pts.push({ x, y, z });
  }

  /** Where a scattered prop meets the ground, for the contact-shadow field
   *  (contactShadows.ts). Handed to the caller on `group.userData.groundSpots`;
   *  these never move, so they are written into the field's static prefix once
   *  and cost no per-frame work at all. */
  const groundSpots: DecorGroundSpot[] = [];

  /** `parts` is a LIST so a prop can be built from several materials (a tree is
   *  bark + canopy) while every part still lands on the same transform — the
   *  matrices are computed once and reused, so the parts can never drift apart,
   *  and each part costs exactly one draw call for the whole map. */
  const instance = (
    parts: PropParts,
    where: { x: number; y: number; z: number }[],
    scaleMin: number,
    scaleMax: number,
    squash = 1,
    lift = 0.3, // fraction of scale to sink/raise (rocks sit half-embedded)
    /** CONTACT SHADOW (contactShadows.ts, 2026-08-05): blob radius as a multiple
     *  of the instance scale, and its strength. 0 = this family casts none —
     *  kelp, because it grows on a seabed under several metres of water where a
     *  crisp ground shadow would be nonsense. Recorded here rather than derived
     *  later because this is the only place that knows each prop's real scale. */
    shadowMul = 0,
    shadowStrength = 0.85,
    /** mapProps.ts swaps meshes carrying this tag for photoscanned models
     *  under the load screen (procedural stays as the fallback). */
    propKind?: string,
  ): void => {
    if (!where.length || !parts.length) return;
    const mats: THREE.Matrix4[] = [];
    const q = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    for (let i = 0; i < where.length; i++) {
      const s = scaleMin + rnd() * (scaleMax - scaleMin);
      if (shadowMul > 0) {
        groundSpots.push({ x: where[i].x, y: where[i].y, z: where[i].z, r: s * shadowMul, s: shadowStrength });
      }
      q.setFromAxisAngle(up, rnd() * Math.PI * 2);
      mats.push(new THREE.Matrix4().compose(
        new THREE.Vector3(where[i].x, where[i].y + s * squash * lift, where[i].z),
        q,
        new THREE.Vector3(s, s * squash, s),
      ));
    }
    for (const [geo, mat] of parts) {
      const im = new THREE.InstancedMesh(geo, mat, where.length);
      for (let i = 0; i < mats.length; i++) im.setMatrixAt(i, mats[i]);
      im.instanceMatrix.needsUpdate = true;
      // a part's geometry can carry its own tag (the tree CROWN is swapped,
      // its trunk is not)
      const tag = propKind ?? (geo.userData.propKind as string | undefined);
      if (tag) { im.userData.propKind = tag; im.userData.propLift = lift; }
      g.add(im);
    }
  };

  const prop = naturalProps(rnd);
  const cloth = new ClothSet();

  if (theme === "day") {
    const rocks = pts.filter((_, i) => i % 3 === 0);
    const bushes = pts.filter((_, i) => i % 3 !== 0);
    instance(prop.rock, rocks, 0.25, 0.7, 0.65, 0.3, 1.05, 0.95, "rock");
    instance(prop.bush, bushes, 0.35, 0.85, 0.75, 0.3, 1.15, 0.8, "bush");
    const trees: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i < 160 && trees.length < 26; i++) {
      const gb = grounds[Math.floor(rnd() * grounds.length)];
      if (!gb) break;
      const x = gb.p[0] + (rnd() - 0.5) * (gb.s[0] - 2);
      const z = gb.p[2] + (rnd() - 0.5) * (gb.s[2] - 2);
      // border band only — keep the combat lanes visually clean
      if (Math.abs(x - cx) < spanX * 0.32 && Math.abs(z - cz) < spanZ * 0.32) continue;
      // practice field: keep the mirror sightlines clear — no tree in front of
      // the glass (one at z=-18 in reality, one in the neon copy)
      if (map.id === "sandlot" && (Math.hypot(x, z + 18) < 9 || Math.hypot(x, z + 18 - PRACTICE_DIM_OFFSET) < 9)) continue;
      const y = gb.p[1] + gb.s[1] / 2;
      // a tree growing through a building's roof breaks the whole illusion
      if (roofed(x, y, z)) continue;
      trees.push({ x, y, z });
    }
    // Two species: ~40% conifers, the rest broadleaf. A mixed tree line reads as
    // a real landscape; a field of one clone reads as an asset. Pines get a
    // narrower scale range (they are taller per unit) so they never out-clip the
    // broadleaf they slot in beside. Both come from the same border-band list, so
    // the roofed/lane guards above already cover them.
    const pines = trees.filter((_, i) => i % 5 < 2);
    const leafy = trees.filter((_, i) => i % 5 >= 2);
    instance(prop.tree, leafy, 0.8, 1.5, 1, 0, 1.55, 0.7);
    instance(prop.pine, pines, 0.85, 1.25, 1, 0, 1.15, 0.7);

    // GRASS TUFTS — a swaying lawn on the GRASS-coloured slabs only (never
    // desert sand, dirt or made ground), so the field stops reading as a flat
    // sheet. Reuses the `roofed` guard (no tufts inside buildings or under
    // overhangs) and stays inside the slab footprint, so a 0.3-0.6 m tuft can
    // never clip a wall. One InstancedMesh for the whole map (one draw call).
    const grassy = (col?: string): boolean => {
      if (!col || col[0] !== "#") return false;
      const n = parseInt(col.slice(1), 16);
      const rr = (n >> 16) & 255, gg = (n >> 8) & 255, bb = n & 255;
      return gg > rr + 6 && gg > bb + 6; // green-dominant = grass/moss
    };
    const grassGrounds = grounds.filter((b) => grassy(b.color));
    if (grassGrounds.length) {
      let area = 0;
      for (const b of grassGrounds) area += b.s[0] * b.s[2];
      const targetTufts = Math.min(900, Math.round(area / 28));
      const tufts: { x: number; y: number; z: number }[] = [];
      for (let i = 0; i < targetTufts * 2 && tufts.length < targetTufts; i++) {
        const gb = grassGrounds[Math.floor(rnd() * grassGrounds.length)];
        if (!gb) break;
        const x = gb.p[0] + (rnd() - 0.5) * (gb.s[0] - 2);
        const z = gb.p[2] + (rnd() - 0.5) * (gb.s[2] - 2);
        const y = gb.p[1] + gb.s[1] / 2;
        if (roofed(x, y, z)) continue;
        tufts.push({ x, y, z });
      }
      // short (knee-high at most, so it never hides a crouched player), but dense
      // enough that the field reads as a lawn rather than occasional clumps.
      instance(prop.grass, tufts, 0.9, 1.7, 1, 0, 0);
    }

    // SEAWEED on any SUBMERGED floor (the pirate seabed) — kelp fronds where
    // land trees would look absurd underwater.
    const seabeds = map.boxes.filter(
      (b) => b.s[0] >= 12 && b.s[2] >= 12 && b.p[1] + b.s[1] / 2 < -2 && notWater(b.color),
    );
    if (seabeds.length) {
      const weeds: { x: number; y: number; z: number }[] = [];
      for (let i = 0; i < 260 && weeds.length < 70; i++) {
        const gb = seabeds[Math.floor(rnd() * seabeds.length)];
        if (!gb) break;
        weeds.push({
          x: gb.p[0] + (rnd() - 0.5) * (gb.s[0] - 4),
          y: gb.p[1] + gb.s[1] / 2,
          z: gb.p[2] + (rnd() - 0.5) * (gb.s[2] - 4),
        });
      }
      instance(prop.kelp, weeds, 0.7, 1.7, 1, 0);
    }
  } else {
    const rocks = pts.filter((_, i) => i % 3 === 0);
    const bollards = pts.filter((_, i) => i % 3 !== 0);
    instance(prop.darkRock, rocks, 0.25, 0.6, 0.65, 0.3, 1.05, 0.95, "darkRock");
    instance(prop.bollard, bollards, 0.7, 1.1, 1, 0.3, 0.5, 0.9);
  }

  // ---- Skyhaven only: neon ads on the towers + cozy apartment interiors ----
  if (map.id === "neo_island" || map.id === "rooftop_spiderweb") {
    addSkyhavenDressing(g, map, rnd, roofed);
  }
  if (map.id === "wild_west_train_pursuit") {
    addWildWestDressing(g);
  }
  if (map.id === "medieval_castle") {
    addMedievalDressing(g, cloth);
  }

  // --- CLOTH: banners on the tall walls of EVERY map (generic, from the map's
  // own boxes), plus whatever the per-map dressings hung above. Two merged
  // meshes for the whole map.
  const palette = theme === "day"
    ? ["#8c1f2d", "#244d7c", "#2f6b3a", "#c2a029", "#d8cfbd", "#6b2f7a"]
    : ["#ff2e88", "#22d9ff", "#ffd12e", "#9b5cff", "#1de08f", "#ff6a2e"];
  for (const b of bannerSpots(map, rnd)) {
    cloth.add(b.w, b.h, "top", b.x, b.y, b.z, b.yaw, palette[Math.floor(rnd() * palette.length)]);
  }
  cloth.build(g);

  mergeStaticDecor(g);
  // where every scattered prop touches the ground — see `groundSpots` above
  g.userData.groundSpots = groundSpots;
  return g;
}

/** Collapse the hundreds of individual STATIC decor meshes a per-map dressing
 *  builds (addWildWestDressing alone is ~300 boxes) into ONE merged mesh per
 *  (material, shadow-flags, vertex-layout) — the SAME draw-call win the static
 *  MAP boxes already get (renderer.ts loadMap merge). Each mesh's world matrix
 *  is baked into a cloned geometry, so the merged result is triangle-for-
 *  triangle identical; only the draw-call count drops (and the shadow pass
 *  shrinks with it). Runs once at map load, off the frame path.
 *
 *  SKIPS anything that can't safely merge: per-frame animated transforms
 *  (userData.spin / eq / beatRing), textured or transparent materials (signs,
 *  neon ads, the histogram facades, glass — each has a unique or blended
 *  material anyway, so it stays a singleton), InstancedMesh / Line / Points /
 *  Sprite, and any material-array mesh. A lone mesh for a material is left in
 *  place (merging one geometry saves nothing). Exported for the offline
 *  correctness bench (agent/bench-decor-merge.mjs — pure three, no GPU).
 *
 *  NB: g is DETACHED here (buildDecor has not returned it yet), so
 *  updateWorldMatrix yields g-LOCAL coordinates; the merged mesh is re-added to
 *  g, so it stays correct wherever the caller later places g. */
export function mergeStaticDecor(g: THREE.Group): void {
  type Bucket = { mat: THREE.Material; cast: boolean; receive: boolean; geos: THREE.BufferGeometry[]; victims: THREE.Mesh[] };
  const buckets = new Map<string, Bucket>();
  g.traverse((o) => {
    const m = o as THREE.Mesh & { isMesh?: boolean; isInstancedMesh?: boolean };
    if (!m.isMesh || m.isInstancedMesh) return;
    if (Array.isArray(m.material) || !m.material) return;
    if (o.userData.spin || o.userData.eq !== undefined || o.userData.beatRing) return;
    const mat = m.material as THREE.MeshStandardMaterial;
    if (mat.transparent || mat.map) return;
    const geo = m.geometry;
    if (!geo || !geo.getAttribute("position")) return;
    const attrSig = Object.keys(geo.attributes).sort().join(",");
    const key = `${mat.uuid}|${m.castShadow ? 1 : 0}|${m.receiveShadow ? 1 : 0}|${attrSig}`;
    let b = buckets.get(key);
    if (!b) { b = { mat, cast: m.castShadow, receive: m.receiveShadow, geos: [], victims: [] }; buckets.set(key, b); }
    m.updateWorldMatrix(true, false);
    const baked = geo.index ? geo.toNonIndexed() : geo.clone();
    baked.applyMatrix4(m.matrixWorld);
    b.geos.push(baked);
    b.victims.push(m);
  });
  for (const b of buckets.values()) {
    if (b.victims.length < 2) { for (const geo of b.geos) geo.dispose(); continue; } // singleton — leave in place
    const merged = mergeGeometries(b.geos, false);
    for (const geo of b.geos) geo.dispose();
    if (!merged) continue; // incompatible attributes → leave the originals untouched
    for (const v of b.victims) { v.parent?.remove(v); v.geometry.dispose(); }
    const mesh = new THREE.Mesh(merged, b.mat);
    mesh.castShadow = b.cast;
    mesh.receiveShadow = b.receive;
    g.add(mesh);
  }
}

function signTexture(text: string, bg = "#6a3d22", fg = "#f4d28f"): THREE.CanvasTexture {
  const cvs = document.createElement("canvas");
  cvs.width = 512; cvs.height = 160;
  const c = cvs.getContext("2d")!;
  c.fillStyle = bg;
  c.fillRect(0, 0, cvs.width, cvs.height);
  c.strokeStyle = "#27170e";
  c.lineWidth = 12;
  c.strokeRect(10, 10, cvs.width - 20, cvs.height - 20);
  c.fillStyle = "rgba(0,0,0,0.18)";
  for (let x = 35; x < cvs.width; x += 42) c.fillRect(x, 8, 5, cvs.height - 16);
  c.font = text.length > 9 ? "bold 54px serif" : "bold 66px serif";
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.lineWidth = 5;
  c.strokeStyle = "#2b170c";
  c.fillStyle = fg;
  c.strokeText(text, cvs.width / 2, cvs.height / 2 + 4);
  c.fillText(text, cvs.width / 2, cvs.height / 2 + 4);
  const tex = new THREE.CanvasTexture(cvs);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function addWildWestDressing(g: THREE.Group): void {
  const wood = new THREE.MeshStandardMaterial({ color: "#6b3f24", roughness: 0.86, metalness: 0.03 });
  const darkWood = new THREE.MeshStandardMaterial({ color: "#332015", roughness: 0.92, metalness: 0.02 });
  const roof = new THREE.MeshStandardMaterial({ color: "#9b6737", roughness: 0.78, metalness: 0.05 });
  const iron = new THREE.MeshStandardMaterial({ color: "#25272a", roughness: 0.48, metalness: 0.7 });
  const canvasMat = new THREE.MeshStandardMaterial({ color: "#c9b273", roughness: 0.95, metalness: 0.01 });
  const glass = new THREE.MeshStandardMaterial({ color: "#96c7d4", roughness: 0.22, metalness: 0.03, transparent: true, opacity: 0.68 });
  const box = (w: number, h: number, d: number, mat: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    g.add(m);
    return m;
  };
  const cyl = (r: number, h: number, mat: THREE.Material, x: number, y: number, z: number, seg = 12): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, seg), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    g.add(m);
    return m;
  };
  const sign = (text: string, x: number, y: number, z: number, w: number, h: number, bg?: string, yaw = Math.PI): void => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ map: signTexture(text, bg), side: THREE.DoubleSide, toneMapped: false }),
    );
    m.position.set(x, y, z);
    m.rotation.y = yaw;
    g.add(m);
  };

  const town: [number, number, number, number, string, string, "north" | "south"][] = [
    [88, -70, 22, 14, "SALOON", "#7b2d1e", "north"],
    [116, -70, 18, 14, "BANK", "#5d3d22", "north"],
    [63, -70, 18, 12, "SHERIFF", "#29435d", "north"],
    [91, -45, 20, 14, "HOTEL", "#704225", "south"],
    [116, -45, 18, 12, "GENERAL STORE", "#6a4a2a", "south"],
    [38, -42, 18, 12, "GUNSMITH", "#49311f", "south"],
    [139, -58, 18, 24, "STABLE", "#684322", "south"],
    // central town square (faces toward the plaza)
    [-2, -26, 18, 24, "CHURCH", "#6a5a41", "north"],
    [2, 40, 18, 14, "CANTINA", "#7b4a1e", "south"],
    [-34, -16, 16, 12, "TRADING POST", "#5d432b", "north"],
    [34, -14, 14, 10, "TELEGRAPH", "#3d4a5d", "north"],
  ];
  for (const [x, z, w, d, label, bg, face] of town) {
    const out = face === "north" ? 1 : -1;
    const yaw = face === "north" ? 0 : Math.PI;
    const frontZ = z + out * (d / 2 + 0.12);
    // Western false-front facade rising above the box roof, with plank trim.
    box(w + 1.4, 2.3, 0.35, wood, x, 4.45, frontZ);
    box(w + 1.9, 0.34, 0.52, darkWood, x, 5.78, frontZ + out * 0.06);
    box(w + 0.9, 0.28, 0.42, darkWood, x, 3.36, frontZ + out * 0.05);
    for (let py = 3.65; py <= 5.35; py += 0.42) box(w + 1.0, 0.055, 0.42, darkWood, x, py, frontZ + out * 0.09);
    sign(label, x, 4.76, frontZ + out * 0.25, Math.min(w - 2, 10.8), 1.55, bg, yaw);
    // Boardwalk porch, awning and posts.
    box(w + 4, 0.22, 4.1, darkWood, x, 0.2, frontZ + out * 2.0);
    box(w + 3.4, 0.28, 3.6, roof, x, 2.78, frontZ + out * 2.0);
    for (let px = x - w / 2 + 1.3; px <= x + w / 2 - 1.2; px += 4.2) {
      box(0.22, 2.5, 0.22, darkWood, px, 1.48, frontZ + out * 3.55);
      box(0.3, 1.1, 0.12, darkWood, px, 1.05, frontZ + out * 4.8); // hitching rail post
    }
    box(w + 2.6, 0.15, 0.18, darkWood, x, 1.25, frontZ + out * 4.8);
    // Doors and windows as real separate materials, not texture noise.
    box(1.55, 2.35, 0.12, darkWood, x, 1.45, frontZ + out * 0.28);
    box(1.35, 1.05, 0.12, glass, x - w * 0.28, 2.0, frontZ + out * 0.3);
    box(1.35, 1.05, 0.12, glass, x + w * 0.28, 2.0, frontZ + out * 0.3);
    box(0.24, 1.25, 0.16, darkWood, x - w * 0.28 - 0.86, 2.0, frontZ + out * 0.36);
    box(0.24, 1.25, 0.16, darkWood, x - w * 0.28 + 0.86, 2.0, frontZ + out * 0.36);
    box(0.24, 1.25, 0.16, darkWood, x + w * 0.28 - 0.86, 2.0, frontZ + out * 0.36);
    box(0.24, 1.25, 0.16, darkWood, x + w * 0.28 + 0.86, 2.0, frontZ + out * 0.36);
  }

  // Saloon-specific swing doors and balcony rail.
  for (const sx of [-0.45, 0.45]) {
    const door = box(0.62, 1.25, 0.09, darkWood, 88 + sx, 1.25, -62.36);
    door.rotation.y = sx * 0.22;
  }
  box(20, 0.18, 0.22, darkWood, 88, 3.25, -61.4);
  for (let x = 79.5; x <= 96.5; x += 2.2) box(0.16, 0.8, 0.16, darkWood, x, 3.0, -61.4);

  // No depot fronts around the train ring: the old roof/sign/posts read as a
  // small arch overlapping the track from gameplay camera angles.

  // Water towers and maintenance props.
  for (const [x, z] of [[75, -97], [-72, 78], [124, 12]] as const) {
    for (const dx of [-1.4, 1.4]) for (const dz of [-1.4, 1.4]) box(0.28, 5.4, 0.28, darkWood, x + dx, 2.7, z + dz);
    const tank = cyl(2.1, 2.1, wood, x, 6.2, z, 18);
    tank.rotation.y = Math.PI / 10;
    box(5.4, 0.24, 0.28, darkWood, x, 7.35, z);
    box(0.26, 3.0, 0.26, darkWood, x + 2.7, 3.1, z);
    const spout = box(0.35, 0.35, 3.0, iron, x + 2.7, 5.6, z - 1.4);
    spout.rotation.x = -0.35;
  }

  // Mine and bandit camp staging.
  sign("GOLD MINE", -36, 6.9, 105.7, 7.8, 1.35, "#3d2b1c");
  for (let i = 0; i < 5; i++) {
    const cart = box(1.2, 0.7, 1.8, iron, -22 + i * 2.8, 0.85, 123 + (i % 2) * 1.2);
    cart.rotation.y = 0.15 * (i - 2);
    for (const sx of [-0.48, 0.48]) for (const sz of [-0.65, 0.65]) {
      const wh = cyl(0.18, 0.14, darkWood, cart.position.x + sx, 0.42, cart.position.z + sz, 10);
      wh.rotation.z = Math.PI / 2;
    }
  }
  for (const [x, z] of [[36, 113], [43, 118], [49, 112]] as const) {
    const tent = new THREE.Mesh(new THREE.ConeGeometry(2.2, 2.8, 4), canvasMat);
    tent.position.set(x, 1.45, z);
    tent.rotation.y = Math.PI / 4;
    tent.castShadow = true;
    g.add(tent);
    cyl(0.18, 1.8, darkWood, x, 0.9, z, 8);
  }

  // Telegraph wires along the road/rail corridor.
  const polePts: THREE.Vector3[] = [];
  for (let z = -120; z <= 118; z += 24) polePts.push(new THREE.Vector3(26, 6.5, z));
  const wireMat = new THREE.LineBasicMaterial({ color: "#2a2118", transparent: true, opacity: 0.85 });
  for (let i = 1; i < polePts.length; i++) {
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([polePts[i - 1].clone().add(new THREE.Vector3(-1.6, 0, 0)), polePts[i].clone().add(new THREE.Vector3(-1.6, 0, 0))]), wireMat));
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([polePts[i - 1].clone().add(new THREE.Vector3(1.6, -0.1, 0)), polePts[i].clone().add(new THREE.Vector3(1.6, -0.1, 0))]), wireMat));
  }

  // Small repeated frontier props near the street and depots.
  for (const [x, z] of [[80, -80], [84, -80], [119, -81], [61, -37], [129, -49], [93, -35], [84, 3], [-87, 4], [4, -88]] as const) {
    cyl(0.38, 0.75, wood, x, 0.38, z, 12);
    box(1.4, 0.55, 1.2, darkWood, x + 1.25, 0.3, z + 0.7);
  }

  // Extra unmistakably Western set dressing: wagons, gallows, corrals,
  // graves, campfires, crates and desert bones.
  const addCoveredWagon = (x: number, z: number, yaw = 0): void => {
    const wagon = new THREE.Group();
    const localBox = (w: number, h: number, d: number, mat: THREE.Material, px: number, py: number, pz: number): void => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(px, py, pz); m.castShadow = true; m.receiveShadow = true; wagon.add(m);
    };
    localBox(2.4, 0.45, 4.0, wood, 0, 0.75, 0);
    for (const sx of [-1.3, 1.3]) for (const sz of [-1.55, 1.55]) {
      const wh = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.08, 8, 16), darkWood);
      wh.rotation.y = Math.PI / 2; wh.position.set(sx, 0.46, sz); wagon.add(wh);
    }
    const cover = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.15, 3.8, 14, 1, true, 0, Math.PI), canvasMat);
    cover.rotation.z = Math.PI / 2; cover.rotation.y = Math.PI / 2; cover.position.set(0, 1.45, 0); wagon.add(cover);
    wagon.position.set(x, 0, z); wagon.rotation.y = yaw; g.add(wagon);
  };
  addCoveredWagon(70, -84, 0.5);
  addCoveredWagon(-118, -64, -0.8);
  addCoveredWagon(54, 96, 1.2);
  // Gallows by the jail — its old spot (52,-48) was radius ~71 from center,
  // inside the train ring (r=74 ± wagon width), so the train drove through it.
  box(6, 0.3, 6, darkWood, 76, 0.15, -33); // gallows deck
  box(0.35, 4.6, 0.35, darkWood, 73.5, 2.3, -33);
  box(0.35, 4.6, 0.35, darkWood, 78.5, 2.3, -33);
  box(5.7, 0.35, 0.35, darkWood, 76, 4.55, -33);
  cyl(0.045, 1.4, canvasMat, 76, 3.75, -33, 8);
  for (const [x, z] of [[-100, -118], [-96, -118], [-92, -118], [-100, -123], [-96, -123]] as const) {
    box(0.6, 1.0, 0.18, darkWood, x, 0.5, z);
    box(1.0, 0.16, 0.16, darkWood, x, 0.88, z);
  }
  for (const [x, z, w, d] of [[132, -80, 22, 16], [-138, 68, 24, 18], [146, 94, 20, 16]] as const) {
    box(w, 0.28, 0.26, darkWood, x, 1.0, z - d / 2);
    box(w, 0.28, 0.26, darkWood, x, 1.0, z + d / 2);
    box(0.26, 0.28, d, darkWood, x - w / 2, 1.0, z);
    box(0.26, 0.28, d, darkWood, x + w / 2, 1.0, z);
  }
  for (const [x, z] of [[-150, 24], [-142, 36], [152, 12], [136, -28], [-50, 130]] as const) {
    box(1.2, 0.12, 0.18, canvasMat, x, 0.2, z);
    box(0.18, 0.12, 1.0, canvasMat, x + 0.7, 0.2, z + 0.2);
  }
}

function addMedievalDressing(g: THREE.Group, cloth: ClothSet): void {
  const stone = new THREE.MeshStandardMaterial({ color: "#5f5b54", roughness: 0.88, metalness: 0.03 });
  const wood = new THREE.MeshStandardMaterial({ color: "#5b3724", roughness: 0.86, metalness: 0.02 });
  const darkWood = new THREE.MeshStandardMaterial({ color: "#2e1d14", roughness: 0.92, metalness: 0.02 });
  const RED = "#8c1f2d", BLUE = "#244d7c";
  const hay = new THREE.MeshStandardMaterial({ color: "#b99a4e", roughness: 0.95, metalness: 0.01 });
  const flame = new THREE.MeshBasicMaterial({ color: "#ffb02e" });
  const box = (w: number, h: number, d: number, mat: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    g.add(m);
    return m;
  };
  const cyl = (r: number, h: number, mat: THREE.Material, x: number, y: number, z: number, seg = 10): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, seg), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    g.add(m);
    return m;
  };

  // Flags and banners make every tower/wall read as finished rather than raw
  // boxes. These were the ONLY cloth in the game and they were flat 0.1 m
  // slabs — a banner drawn as a box. Now real draped sheets on the shared
  // `cloth` material, hemmed, swallowtailed and moving in the wind; the mast
  // still gets its own wooden pole because a flag flies off ITS side.
  for (const [x, z, col] of [[-48, -48, RED], [48, -48, BLUE], [-48, 48, BLUE], [48, 48, RED]] as const) {
    cyl(0.08, 4.5, darkWood, x, 19.0, z, 8);
    // fly out along +x/-x from the mast, away from the keep
    // a "left"-anchored sheet flies along its LOCAL +x, so yaw 0 flies +X and
    // yaw π flies -X: each corner tower's flag streams away from the keep.
    cloth.add(2.4, 1.3, "left", x + Math.sign(x) * 0.08, 20.5, z, Math.sign(x) > 0 ? 0 : Math.PI, col, false);
  }
  for (let x = -36; x <= 36; x += 18) {
    cloth.add(2.0, 3.4, "top", x, 8.4, 50.3, 0, x % 36 === 0 ? RED : BLUE);
    cloth.add(2.0, 3.4, "top", x, 8.4, -50.3, Math.PI, x % 36 === 0 ? BLUE : RED);
  }
  for (let z = -30; z <= 30; z += 18) {
    cloth.add(2.0, 3.4, "top", -50.3, 8.4, z, -Math.PI / 2, z % 36 === 0 ? RED : BLUE);
    cloth.add(2.0, 3.4, "top", 50.3, 8.4, z, Math.PI / 2, z % 36 === 0 ? BLUE : RED);
  }

  // Torches at entrances, wall walks and the dungeon/tunnel exits.
  for (const [x, y, z] of [[-8, 2.2, -44], [8, 2.2, -44], [-16, 2.5, 3], [16, 2.5, 3], [44, -2.4, 38], [44, -2.4, 52], [-32, 2.2, 16], [32, 2.2, 14]] as const) {
    cyl(0.07, 0.8, darkWood, x, y, z, 8);
    const f = new THREE.Mesh(new THREE.ConeGeometry(0.23, 0.55, 8), flame);
    f.position.set(x, y + 0.55, z);
    g.add(f);
  }

  // Moat and drawbridge finishing: posts, chain hints and stone approach walls.
  // posts + rails sit CLEAR of the 12-wide deck (they crowded the entrance)
  for (const x of [-7.6, 7.6]) {
    for (const z of [-72, -66, -58, -52]) {
      cyl(0.16, 1.15, darkWood, x, 0.58, z, 8);
    }
    box(0.12, 0.12, 20, darkWood, x, 1.18, -62);
  }
  box(15, 0.28, 0.3, stone, 0, 0.28, -74.4);
  box(15, 0.28, 0.3, stone, 0, 0.28, -53.6);

  // Courtyard/village clutter and stables.
  for (const [x, z] of [[-36, -25], [-30, -28], [25, -27], [31, -26], [-62, -99], [-40, -111], [54, -92], [78, -115]] as const) {
    box(2.4, 1.1, 1.4, hay, x, 0.58, z);
    box(1.4, 0.9, 1.2, darkWood, x + 2.2, 0.48, z + 0.6);
  }
  for (const [x, z] of [[-12, 28], [14, 30], [-76, -95], [66, -108]] as const) {
    box(5.4, 0.3, 2.6, wood, x, 1.0, z);
    box(0.28, 1.6, 0.28, darkWood, x - 2.5, 1.15, z - 1.1);
    box(0.28, 1.6, 0.28, darkWood, x + 2.5, 1.15, z - 1.1);
    box(5.8, 0.25, 3.1, hay, x, 2.05, z);
  }
  // (the 18 loose ground rails that used to litter the north field are gone —
  // they read as random sticks, not as anything built)

  // ---- LAMPADAIRES: wooden lamp posts with hanging lanterns — line the
  // roads and the market town so the fields read as lived-in, not empty.
  const iron = new THREE.MeshStandardMaterial({ color: "#26282c", roughness: 0.5, metalness: 0.7 });
  const lanternGlow = new THREE.MeshBasicMaterial({ color: "#ffc76a" });
  const lampPost = (x: number, z: number, side = 1): void => {
    box(0.2, 3.4, 0.2, darkWood, x, 1.7, z);                 // post
    box(0.95, 0.15, 0.15, darkWood, x + side * 0.38, 3.32, z); // arm toward the road
    const lx = x + side * 0.75;
    box(0.42, 0.1, 0.42, iron, lx, 3.05, z);                 // lantern cap
    box(0.3, 0.42, 0.3, lanternGlow, lx, 2.78, z);           // glowing pane
    box(0.36, 0.08, 0.36, iron, lx, 2.52, z);                // lantern base
  };
  // south road through the bourg (staggered pairs), lantern arms over the road
  for (let z = 76; z <= 136; z += 12) { lampPost(-7.6, z, 1); lampPost(7.6, z + 6, -1); }
  // north gate road, between the moat and the villages (the stretch nearer
  // the wall is gatehouse towers + drawbridge levers — no room for posts)
  for (const [lx, lz] of [[-10, -78], [-10, -86], [-10, -94], [10, -82], [10, -90], [10, -98]] as const) {
    lampPost(lx, lz, lx < 0 ? 1 : -1);
  }
  // market square corners + by the well
  lampPost(12, 78, -1); lampPost(-12, 78, 1); lampPost(12, 98, -1); lampPost(-12, 98, 1);
  // village lane
  lampPost(-24, -98, 1); lampPost(20, -98, -1); lampPost(-70, -96, 1); lampPost(74, -96, -1);

  // Market-square life: banners on the north row, crates + hay by the stalls,
  // and a hand cart parked at the road edge.
  cloth.add(2.0, 3.0, "top", -20, 5.7, 75.65, 0, RED);  // on the north row's outer face
  cloth.add(2.0, 3.0, "top", 20, 5.7, 75.65, 0, BLUE);
  for (const [cx, cz] of [[14.6, 86.5], [15.4, 88.2], [-14, 89]] as const) {
    box(1.1, 0.9, 1.1, darkWood, cx, 0.45, cz);
  }
  box(2.2, 1.0, 1.6, hay, -14.5, 0.5, 92.5);
  box(2.2, 0.35, 4.2, wood, 8.2, 0.95, 124);    // hand cart bed
  box(0.3, 0.9, 0.3, darkWood, 7.4, 0.45, 126); // cart legs
  box(0.3, 0.9, 0.3, darkWood, 9.0, 0.45, 126);
  for (const wx of [7.3, 9.1]) {
    const wheel = cyl(0.55, 0.14, darkWood, wx, 0.55, 122.6, 12);
    wheel.rotation.z = Math.PI / 2;
  }
}

/** Neon advertisement billboards + furnished apartment floors (Skyhaven). */
function addSkyhavenDressing(
  g: THREE.Group,
  map: MapDef,
  rnd: () => number,
  roofed: (x: number, y: number, z: number) => boolean,
): void {
  // -- the towers (same roof-slab detection as the cable system) --
  const cands = map.boxes
    .filter((b) => b.s[1] <= 2 && Math.min(b.s[0], b.s[2]) >= 6 && b.p[1] > 14)
    .sort((x, y) => y.p[1] + y.s[1] / 2 - (x.p[1] + x.s[1] / 2));
  const towers: typeof cands = [];
  for (const c of cands) {
    if (towers.every((t) => Math.hypot(t.p[0] - c.p[0], t.p[2] - c.p[2]) > 25)) towers.push(c);
    if (towers.length === 6) break;
  }

  // -- MUSIC HISTOGRAM SKIN on the ONE facade of every tower that faces the
  // train ring: a shared canvas texture (redrawn to the live spectrum by the
  // renderer) mapped onto a panel over the real exterior wall. Roof slabs are
  // only caps/markers on Skyhaven, so never derive facade size from them.
  const histCanvas = document.createElement("canvas");
  histCanvas.width = 256; histCanvas.height = 256;
  const histTex = new THREE.CanvasTexture(histCanvas);
  histTex.colorSpace = THREE.SRGBColorSpace;
  const histMat = new THREE.MeshBasicMaterial({ map: histTex, toneMapped: false, transparent: true, side: THREE.DoubleSide, depthWrite: false });
  type HistFace = { x: number; z: number; width: number; height: number; nx: number; nz: number };
  const towerWallFaces = (): HistFace[] => {
    const trainCx = map.train?.cx ?? 0;
    const trainCz = map.train?.cz ?? 0;
    const tallWalls = map.boxes.filter((b) => b.s[1] >= 18 && Math.min(b.s[0], b.s[2]) <= 0.8);
    const xWalls = tallWalls.filter((b) => b.s[0] <= 0.8 && b.s[2] >= 6);
    const zWalls = tallWalls.filter((b) => b.s[2] <= 0.8 && b.s[0] >= 1.2);
    const faces: HistFace[] = [];
    const seen: [number, number][] = [];
    const sameTower = (cx: number, cz: number): boolean => seen.some(([x, z]) => Math.hypot(x - cx, z - cz) < 2.5);
    const zFace = (cx: number, cz: number, d: number, h: number, dir: 1 | -1): HistFace | null => {
      const ws = zWalls.filter((w) =>
        Math.abs(w.s[1] - h) < 0.75 &&
        Math.abs(Math.abs(w.p[2] - cz) - d / 2) < 0.9 &&
        Math.sign(w.p[2] - cz) === dir &&
        Math.abs(w.p[0] - cx) < d + 8);
      if (!ws.length) return null;
      let minX = Infinity, maxX = -Infinity, zSum = 0;
      for (const w of ws) {
        minX = Math.min(minX, w.p[0] - w.s[0] / 2);
        maxX = Math.max(maxX, w.p[0] + w.s[0] / 2);
        zSum += w.p[2];
      }
      return { x: (minX + maxX) / 2, z: zSum / ws.length, width: maxX - minX, height: h, nx: 0, nz: dir };
    };
    const pickFace = (options: HistFace[], cx: number, cz: number): HistFace => {
      const dx = trainCx - cx, dz = trainCz - cz;
      const len = Math.hypot(dx, dz) || 1;
      const ux = dx / len, uz = dz / len;
      return options.sort((a, b) => (b.nx * ux + b.nz * uz) - (a.nx * ux + a.nz * uz))[0];
    };
    for (let i = 0; i < xWalls.length; i++) {
      for (let j = i + 1; j < xWalls.length; j++) {
        const a = xWalls[i], b = xWalls[j];
        if (Math.abs(a.p[2] - b.p[2]) > 0.9 || Math.abs(a.s[1] - b.s[1]) > 0.75 || Math.abs(a.p[1] - b.p[1]) > 0.75) continue;
        const w = Math.abs(a.p[0] - b.p[0]);
        if (w < 8 || w > 24) continue;
        const left = a.p[0] < b.p[0] ? a : b;
        const right = a.p[0] < b.p[0] ? b : a;
        const cx = (left.p[0] + right.p[0]) / 2;
        const cz = (left.p[2] + right.p[2]) / 2;
        if (sameTower(cx, cz)) continue;
        seen.push([cx, cz]);
        const h = (left.s[1] + right.s[1]) / 2;
        const d = (left.s[2] + right.s[2]) / 2;
        const options: HistFace[] = [
          { x: right.p[0], z: right.p[2], width: right.s[2], height: h, nx: 1, nz: 0 },
          { x: left.p[0], z: left.p[2], width: left.s[2], height: h, nx: -1, nz: 0 },
        ];
        const front = zFace(cx, cz, d, h, -1);
        const back = zFace(cx, cz, d, h, 1);
        if (front) options.push(front);
        if (back) options.push(back);
        faces.push(pickFace(options, cx, cz));
      }
    }
    return faces;
  };
  let firstHist = true;
  for (const face of towerWallFaces()) {
    const panelW = Math.max(5, face.width - 1.0);
    const panelH = Math.max(8, face.height - 3.5);
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(panelW, panelH), histMat);
    panel.position.set(face.x + face.nx * 0.36, 2 + panelH / 2, face.z + face.nz * 0.36);
    panel.rotation.y = Math.atan2(face.nx, face.nz);
    panel.renderOrder = 2;
    if (firstHist) { panel.userData.histViz = { canvas: histCanvas, texture: histTex }; firstHist = false; }
    g.add(panel);
  }

  // -- NEON ADS: canvas-textured billboards bolted to the facades --
  const AD_TEXTS = ["CHIBRE STATION", "NEON PURGE", "ZARYA COLA", "SKY NOODLES", "HOTEL PURGE", "GUN GAME 24/7"];
  const AD_COLORS = ["#ff2fd0", "#00f0ff", "#ffd166", "#9dff00", "#ff5a2a", "#b39dff"];
  const makeAd = (text: string, color: string): THREE.CanvasTexture => {
    const cvs = document.createElement("canvas");
    cvs.width = 512; cvs.height = 128;
    const c2 = cvs.getContext("2d")!;
    c2.fillStyle = "#0a0c14";
    c2.fillRect(0, 0, 512, 128);
    c2.strokeStyle = color; c2.lineWidth = 6; c2.strokeRect(6, 6, 500, 116);
    c2.font = "bold 54px monospace";
    c2.textAlign = "center"; c2.textBaseline = "middle";
    c2.shadowColor = color; c2.shadowBlur = 26;
    c2.fillStyle = color;
    c2.fillText(text, 256, 68);
    c2.shadowBlur = 8;
    c2.fillStyle = "#ffffff";
    c2.fillText(text, 256, 68);
    const tex = new THREE.CanvasTexture(cvs);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  };
  // walls extend past the roof slabs — walk every sign OUTWARD until it
  // clears the facade (same lesson as the elevators: roof ≠ footprint)
  const insideWall = (x: number, y: number, z: number): boolean =>
    map.boxes.some((b) => {
      const top = b.p[1] + b.s[1] / 2, bot = b.p[1] - b.s[1] / 2;
      return y > bot && y < top &&
        Math.abs(x - b.p[0]) < b.s[0] / 2 + 0.12 &&
        Math.abs(z - b.p[2]) < b.s[2] / 2 + 0.12;
    });
  const faceDefs: { ax: "x" | "z"; dir: number; rotY: number }[] = [
    { ax: "x", dir: 1, rotY: Math.PI / 2 },
    { ax: "x", dir: -1, rotY: -Math.PI / 2 },
    { ax: "z", dir: 1, rotY: 0 },
    { ax: "z", dir: -1, rotY: Math.PI },
  ];
  const clearPos = (t: { p: [number, number, number]; s: [number, number, number] }, f: { ax: "x" | "z"; dir: number }, y: number): [number, number, number] => {
    let px = t.p[0], pz = t.p[2];
    const half = f.ax === "x" ? t.s[0] / 2 : t.s[2] / 2;
    let off = half + 0.3;
    for (let i = 0; i < 24; i++) {
      px = f.ax === "x" ? t.p[0] + f.dir * off : t.p[0];
      pz = f.ax === "z" ? t.p[2] + f.dir * off : t.p[2];
      if (!insideWall(px, y, pz)) break;
      off += 0.5;
    }
    return [px, y, pz];
  };
  let adIdx = 0;
  const stripGeos: THREE.BufferGeometry[] = [];
  towers.forEach((t, ti) => {
    const roofY = t.p[1] + t.s[1] / 2;
    const nAds = ti === 0 ? 6 : 4; // FULL of ads — every face carries neon
    for (let i = 0; i < nAds; i++) {
      const text = AD_TEXTS[adIdx % AD_TEXTS.length];
      const color = AD_COLORS[adIdx % AD_COLORS.length];
      adIdx++;
      const f = faceDefs[(ti + i) % faceDefs.length];
      const faceW = f.ax === "x" ? t.s[2] : t.s[0];
      const wide = Math.min(12, Math.max(7, faceW * 0.9));
      // CHIBRE STATION crowns the tallest tower, others stagger down the wall
      // stagger down the facade and CLAMP to the building — with 4-6 signs
      // the old 0.4+0.25*i formula walked right past the roof into the sky
      const y = ti === 0 && i === 0
        ? roofY - 2.5
        : Math.min(roofY - 2, Math.max(6, roofY * (0.2 + (0.65 * i) / Math.max(1, nAds - 1)) + rnd() * 1.5));
      const pos = clearPos(t, f, y);
      const sign = new THREE.Mesh(
        new THREE.PlaneGeometry(wide, wide * 0.25),
        new THREE.MeshBasicMaterial({ map: makeAd(text, color), toneMapped: false, side: THREE.DoubleSide }),
      );
      sign.position.set(pos[0] + (f.ax === "x" ? f.dir * 0.05 : 0), pos[1], pos[2] + (f.ax === "z" ? f.dir * 0.05 : 0));
      sign.rotation.y = f.rotY;
      g.add(sign);
    }
    // LED strips: glowing bands wrapping each face at 1/3 and 2/3 height —
    // the neon-futuristic facade look
    for (const fy of [roofY * 0.34, roofY * 0.67]) {
      for (const f of faceDefs) {
        const pos = clearPos(t, f, fy);
        const faceW = (f.ax === "x" ? t.s[2] : t.s[0]) * 0.96;
        const geo = new THREE.BoxGeometry(f.ax === "x" ? 0.12 : faceW, 0.18, f.ax === "x" ? faceW : 0.12);
        geo.translate(pos[0], fy, pos[2]);
        stripGeos.push(geo);
      }
    }
  });
  // -- THE UNDERCITY ARENA: wall-to-wall CHIBRE STATION neon --
  // wall segments sit at ±18 with 8 m tunnel gaps at the axis centres, so
  // the signs mount on the segment midpoints, facing the pit
  const ARENA_HALF = 17.6, SEG = 11.5, ADY = -3.9;
  const arenaSpots: [number, number, number][] = [];
  for (const c of [-SEG, SEG]) {
    arenaSpots.push([ARENA_HALF, c, -Math.PI / 2], [-ARENA_HALF, c, Math.PI / 2]);
  }
  const arenaSpotsZ: [number, number, number][] = [];
  for (const c of [-SEG, SEG]) {
    arenaSpotsZ.push([c, ARENA_HALF, Math.PI], [c, -ARENA_HALF, 0]);
  }
  let ui = 0;
  for (const [sx, sz, ry] of [...arenaSpots.map(([a, b, r]) => [a, b, r] as [number, number, number]), ...arenaSpotsZ]) {
    const text = ui % 3 === 2 ? AD_TEXTS[(ui / 3) % AD_TEXTS.length | 0] : "CHIBRE STATION";
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(9, 2.25),
      new THREE.MeshBasicMaterial({ map: makeAd(text, AD_COLORS[ui % AD_COLORS.length]), toneMapped: false, side: THREE.DoubleSide }),
    );
    sign.position.set(sx, ADY, sz);
    sign.rotation.y = ry;
    g.add(sign);
    ui++;
  }

  if (stripGeos.length) {
    const merged = mergeGeometries(stripGeos.map((x) => x.toNonIndexed()));
    if (merged) g.add(new THREE.Mesh(merged, new THREE.MeshBasicMaterial({ color: "#00f0ff", toneMapped: false })));
    for (const x of stripGeos) x.dispose();
  }

  // (COZY APARTMENTS removed: Skyhaven towers are fully hollow atriums now —
  // the furniture was landing on the roof decks and read as clutter "inside"
  // the buildings. Empty towers: walls, roof, violet grav-lift beam. That's it.)

  // ---- GIANT 80s BOOMBOX + TURNTABLE: colossal landmarks out on the empty
  // beach edges, flanking the arena. Axis-aligned so their nodraw collision
  // boxes (in maps.ts) line up; cassette reels + the vinyl record spin. --
  giantBoombox(g, 0, -94, 0);
  giantTurntable(g, 0, 86, Math.PI);
}

/** Shared neon-synthwave palette for the giant Skyhaven props. */
function retroMats(): Record<string, THREE.Material> {
  return {
    body: new THREE.MeshStandardMaterial({ color: "#20242e", roughness: 0.55, metalness: 0.5 }),
    dark: new THREE.MeshStandardMaterial({ color: "#12141b", roughness: 0.6, metalness: 0.3 }),
    chrome: new THREE.MeshStandardMaterial({ color: "#c2ccd8", roughness: 0.25, metalness: 0.9 }),
    glass: new THREE.MeshStandardMaterial({ color: "#0a0d14", roughness: 0.2, metalness: 0.4 }),
    black: new THREE.MeshStandardMaterial({ color: "#0a0a0c", roughness: 0.35, metalness: 0.2 }),
    mag: new THREE.MeshBasicMaterial({ color: "#ff2ec4", toneMapped: false }),
    cyan: new THREE.MeshBasicMaterial({ color: "#00f0ff", toneMapped: false }),
    amber: new THREE.MeshBasicMaterial({ color: "#ffd166", toneMapped: false }),
  };
}

/** A ~24 m tall ghetto blaster: twin woofers, glowing dust caps, a lit EQ, and
 *  two cassette reels that spin. Built facing local +z, then placed + yawed. */
export function giantBoombox(g: THREE.Group, x: number, z: number, yaw: number): void {
  const grp = new THREE.Group();
  const M = retroMats();
  const box = (w: number, h: number, d: number, mat: THREE.Material, px: number, py: number, pz: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(px, py, pz); grp.add(m); return m;
  };
  // a disc that FACES +z (cylinder's Y axis rotated onto Z) — speaker cones/reels
  const disc = (r: number, depth: number, mat: THREE.Material, px: number, py: number, pz: number): THREE.Mesh => {
    const gm = new THREE.CylinderGeometry(r, r, depth, 28); gm.rotateX(Math.PI / 2);
    const m = new THREE.Mesh(gm, mat); m.position.set(px, py, pz); grp.add(m); return m;
  };
  box(30, 14, 8, M.body, 0, 8, 0);              // chassis
  box(30.4, 14.4, 0.5, M.dark, 0, 8, 4.1);      // front plate
  box(30.6, 0.5, 0.6, M.mag, 0, 15, 4.2);       // top neon trim
  box(30.6, 0.5, 0.6, M.cyan, 0, 1.2, 4.2);     // bottom neon trim
  for (const sx of [-9, 9]) {                    // twin woofers
    disc(5.2, 0.6, M.chrome, sx, 8, 4.2);
    disc(4.4, 0.7, M.dark, sx, 8, 4.4);
    disc(2.6, 0.8, M.body, sx, 8, 4.6);
    disc(1.1, 0.9, sx < 0 ? M.mag : M.cyan, sx, 8, 4.8); // glowing dust cap
    for (let a = 0; a < 8; a++) {
      const ang = (a / 8) * Math.PI * 2;
      box(0.4, 0.4, 0.4, M.chrome, sx + Math.cos(ang) * 4.8, 8 + Math.sin(ang) * 4.8, 4.6);
    }
  }
  box(9.5, 5.4, 0.5, M.glass, 0, 10.4, 4.3);     // cassette window
  for (const rx of [-2.3, 2.3]) {                // spinning reels
    const reel = disc(1.5, 0.5, M.chrome, rx, 10.4, 4.62);
    reel.userData.spin = { axis: "z", speed: 5.5 };
    for (let k = 0; k < 3; k++) {
      const spoke = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.28, 0.3), M.dark);
      spoke.rotation.z = (k * Math.PI) / 3; reel.add(spoke);
    }
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.6, 12), M.dark);
    hub.rotation.x = Math.PI / 2; reel.add(hub);
  }
  // lit EQ bars — a LIVE spectrum visualiser of the map music, driven per
  // frame by the renderer (userData.eq = band index; scale.y grows from the
  // bottom baseline). Geometry pivot is moved to the base so it rises upward.
  for (let i = 0; i < 8; i++) {
    const geo = new THREE.BoxGeometry(0.7, 1, 0.4);
    geo.translate(0, 0.5, 0);
    const bar = new THREE.Mesh(geo, i % 2 ? M.cyan : M.mag);
    bar.position.set(-6.3 + i * 1.8, 3.4, 4.35);
    bar.scale.y = 1.5;
    bar.userData.eq = { band: Math.floor((i / 8) * EQ_BANDS), base: 0.4, max: 4.2 };
    grp.add(bar);
  }
  for (let i = 0; i < 5; i++) box(0.9, 0.9, 0.5, i % 2 ? M.mag : M.amber, -5 + i * 2.5, 13.4, 4.3); // buttons
  box(1.0, 3.2, 1.2, M.chrome, -8, 17.4, 0);     // handle
  box(1.0, 3.2, 1.2, M.chrome, 8, 17.4, 0);
  box(18, 1.0, 1.2, M.chrome, 0, 18.8, 0);
  const ant = box(0.35, 9, 0.35, M.chrome, 12, 20, -2); ant.rotation.z = 0.3; // antenna
  box(0.7, 0.7, 0.7, M.cyan, 13.8, 24.2, -2);
  for (const fx of [-11, 11]) box(3, 1, 4, M.dark, fx, 0.5, 0); // feet
  grp.position.set(x, 0, z); grp.rotation.y = yaw;
  grp.traverse((o) => { o.castShadow = false; o.receiveShadow = false; });
  g.add(grp);
}

/** A ~28 m wide DJ turntable: chrome platter, a black vinyl record (spins) with
 *  a neon label + reflection streak, tonearm, pitch slider and lit controls. */
export function giantTurntable(g: THREE.Group, x: number, z: number, yaw: number): void {
  const grp = new THREE.Group();
  const M = retroMats();
  const box = (w: number, h: number, d: number, mat: THREE.Material, px: number, py: number, pz: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(px, py, pz); grp.add(m); return m;
  };
  const flat = (r: number, h: number, mat: THREE.Material, px: number, py: number, pz: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 40), mat);
    m.position.set(px, py, pz); grp.add(m); return m;
  };
  box(28, 4, 22, M.body, 0, 2, 0);               // plinth
  box(28.4, 0.5, 22.4, M.dark, 0, 4.2, 0);
  box(28.6, 0.5, 0.6, M.mag, 0, 0.7, 11.2);      // front neon trim
  box(28.6, 0.5, 0.6, M.cyan, 0, 3.5, 11.2);
  flat(9.2, 0.7, M.chrome, 0, 4.6, -1.5);        // platter
  // the vinyl record — a flat disc group that spins about Y
  const rec = new THREE.Group(); rec.position.set(0, 5.1, -1.5);
  const add = (mesh: THREE.Mesh): void => { rec.add(mesh); };
  add(new THREE.Mesh(new THREE.CylinderGeometry(8.4, 8.4, 0.3, 48), M.black));      // record
  add(new THREE.Mesh(new THREE.CylinderGeometry(5.8, 5.8, 0.32, 48), new THREE.MeshStandardMaterial({ color: "#17171c", roughness: 0.45 }))); // groove sheen
  const label = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.6, 0.36, 32), M.mag); label.position.y = 0.02; add(label);
  const spindle = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 1.2, 10), M.chrome); spindle.position.y = 0.5; add(spindle);
  const streak = new THREE.Mesh(new THREE.BoxGeometry(8.0, 0.02, 0.7), new THREE.MeshBasicMaterial({ color: "#9fe8ff", transparent: true, opacity: 0.5, toneMapped: false }));
  streak.position.set(2.5, 0.2, 0); add(streak);  // off-centre highlight so the spin reads
  rec.userData.spin = { axis: "y", speed: 2.2 };
  grp.add(rec);
  // tonearm reaching over the record from the back-right pivot
  const pivot = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.0, 2.0, 16), M.chrome); pivot.position.set(8.5, 5.4, 7.5); grp.add(pivot);
  const arm = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 13), M.chrome); arm.position.set(5.5, 5.8, 2.5); arm.rotation.y = 0.7; grp.add(arm);
  box(1.2, 0.8, 1.6, M.dark, 1.6, 5.6, -3.2);    // headshell
  box(0.5, 0.5, 0.5, M.mag, 1.6, 5.2, -4.0);     // stylus
  box(4.5, 0.4, 1.0, M.dark, 9, 4.5, 6);         // pitch-slider track
  box(1.2, 0.7, 1.6, M.chrome, 10, 4.7, 6);      // slider knob
  for (let i = 0; i < 4; i++) box(1.2, 0.8, 1.2, i % 2 ? M.cyan : M.mag, -10 + i * 2.2, 4.7, 7.5); // buttons
  flat(1.4, 0.6, M.amber, -9, 4.6, -6);          // start/stop
  grp.position.set(x, 0, z); grp.rotation.y = yaw;
  grp.traverse((o) => { o.castShadow = false; o.receiveShadow = false; });
  g.add(grp);
}
